import { themedConfirm } from '../../../lib/themed-dialogs';
// NPM Module — správa NGINX Proxy Manager proxy hostů
// UI portováno z PCC Proxy.tsx + ACM multi-instance Settings
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';

let _widgetScope: EffectiveScope = { node: null, identity: null };
export function setNpmWidgetScope(scope: EffectiveScope) {
  _widgetScope = scope;
}
function callMcp<T = unknown>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  return callWidgetTool(name, args, _widgetScope) as Promise<T>;
}
function scopeKey() {
  return _widgetScope.node ?? 'local';
}
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AsyncStatusToggle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DomainFilter,
  EmptyState,
  matchesDomainFilter,
  Modal,
  RefreshButton,
  SearchInput,
  Select,
  StatCard,
  StatsGrid,
  StatusDot,
} from '../../../components/ui';
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Globe,
  Info,
  Layers,
  Link2,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Save,
  Server,
  Shield,
  ShieldOff,
  Star,
  Trash2,
  Wifi,
  WifiOff,
  X,
  XCircle,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Certificate {
  id: number;
  nice_name: string;
  domain_names: string[];
  expires_on: string;
  provider: string;
  meta?: { nginx_online?: boolean };
}

interface ProxyHost {
  id: number;
  domain_names: string[];
  forward_scheme: string;
  forward_host: string;
  forward_port: number;
  ssl_forced: boolean;
  enabled: boolean;
  certificate_id: number | null;
  advanced_config: string;
  allow_websocket_upgrade: boolean;
  block_exploits: boolean;
  caching_enabled: boolean;
  http2_support: boolean;
  hsts_enabled: boolean;
  hsts_subdomains: boolean;
  /** Host založený platformou (network.publish) — v UI jen ke čtení, šedě s vlastníkem. */
  managed_by?: { service: string; instance_ref: string | null };
}

type HaTarget = { host: string; port: string; backup: boolean };

type HostForm = {
  domain: string;
  forward_scheme: string;
  forward_host: string;
  forward_port: string;
  ssl_forced: boolean;
  certificate_id: string;
  advanced_config: string;
  allow_websocket_upgrade: boolean;
  block_exploits: boolean;
  block_blacklist: boolean;
  http2_support: boolean;
  hsts_enabled: boolean;
  publish_upstream: boolean;
  ha_targets: HaTarget[];
};

interface NpmInstance {
  id: string;
  label: string;
  url: string;
  email: string;
  is_upstream?: boolean;
  is_default?: boolean;
}

/** Položka z `npm.proxy_targets` — nabídka cílů pro forward host/port. */
interface ProxyTarget {
  id: string;
  label: string;
  type: 'docker' | 'service' | 'local-host' | 'custom';
  host: string;
  port: number;
  module?: string;
  network?: string;
}

/**
 * Položka z `npm.list_module_hosts`. Systémové služby (Dashboard/API/MCP/Agent)
 * se do stejného pole přidávají s `system: true` a portovými poli navíc —
 * proto jsou tyhle klíče volitelné.
 */
interface ModuleHost {
  module: string;
  instance?: string;
  domain: string;
  host_id: number | null;
  external_url: string;
  source: 'module_config' | 'container_meta' | 'system';
  system?: boolean;
  service_name?: string;
  internal_port?: number;
  external_port?: number;
  alive?: boolean;
}

/** Řádek `acm.proxy_hosts` tak, jak ho vrací `npm.proxy_entries_list`. */
interface ProxyEntry {
  id: string;
  proxy_type: 'http' | 'stream' | 'tty';
  provider: string;
  name: string;
  domain: string;
  target_url: string;
  ssl: boolean;
  managed_by: string | null;
  node_id: string | null;
  enabled: boolean;
  created_at: string;
}

/** Proxy šablona z `npm.templates_list` (registry `proxy_templates`). */
interface ProxyTemplate {
  module: string;
  key: string;
  label: string;
  description: string | null;
  advanced_config: string | null;
  forward_scheme: 'http' | 'https';
  ssl: boolean;
  http2: boolean;
  websockets: boolean;
  block_exploits: boolean;
  hsts_enabled: boolean;
  custom_headers: Array<{ name: string; value: string }>;
  source: 'module-seed' | 'user-edited';
}

/** Stav jednoho terminálu z `localcode.status_get`. */
interface LocalcodeAgentStatus {
  session: boolean;
  ttyd: boolean;
  running: boolean;
  command: string;
  startedAt: string | null;
  port: number;
}

// ── Form helpers ──────────────────────────────────────────────────────────────

const defaultForm = (): HostForm => ({
  domain: '',
  forward_scheme: 'http',
  forward_host: '',
  forward_port: '80',
  ssl_forced: true,
  certificate_id: '0',
  advanced_config: '',
  allow_websocket_upgrade: false,
  block_exploits: true,
  http2_support: false,
  hsts_enabled: false,
  block_blacklist: true,
  publish_upstream: false,
  ha_targets: [],
});

function hostToForm(h: ProxyHost): HostForm {
  return {
    domain: h.domain_names[0] || '',
    forward_scheme: h.forward_scheme,
    forward_host: h.forward_host,
    forward_port: String(h.forward_port),
    ssl_forced: h.ssl_forced,
    certificate_id: String(h.certificate_id ?? 0),
    advanced_config: h.advanced_config || '',
    allow_websocket_upgrade: h.allow_websocket_upgrade,
    block_exploits: h.block_exploits,
    block_blacklist: true,
    http2_support: h.http2_support,
    hsts_enabled: h.hsts_enabled,
    publish_upstream: false,
    ha_targets: [],
  };
}

/** Canonical owner-tool arguments; the browser never reconstructs a REST body. */
function formToToolArgs(f: HostForm) {
  return {
    domain: f.domain.trim(),
    target: `${f.forward_scheme}://${f.forward_host.trim()}:${parseInt(f.forward_port, 10)}`,
    ssl: f.ssl_forced,
    certificate_id: f.certificate_id !== '0' ? f.certificate_id : undefined,
    websocket: f.allow_websocket_upgrade,
    advanced_config: f.advanced_config,
    block_exploits: f.block_exploits,
    block_blacklist: f.block_blacklist,
    http2: f.http2_support,
    hsts_enabled: f.hsts_enabled,
    hsts_subdomains: false,
    publish_upstream: f.publish_upstream,
    targets: f.ha_targets.length > 0 ? f.ha_targets : undefined,
  };
}

/** Update supports only fields owned by the external NPM provider contract. */
function formToUpdateToolArgs(f: HostForm) {
  const {
    block_blacklist: _blockBlacklist,
    publish_upstream: _publishUpstream,
    targets: _targets,
    ...supported
  } = formToToolArgs(f);
  return supported;
}

// ── Shared input style ─────────────────────────────────────────────────────────

const inputCls =
  'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500';

// ── Toggle component (portováno z PCC) ────────────────────────────────────────

function Toggle({ label, desc, checked, onChange }: {
  label: string;
  desc?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className='flex items-start gap-3 cursor-pointer'>
      <div className='relative mt-0.5 shrink-0' onClick={() => onChange(!checked)}>
        <div
          className={`w-9 h-5 rounded-full transition-colors ${
            checked ? 'bg-blue-600' : 'bg-gray-600'
          }`}
        >
          <div
            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              checked ? 'translate-x-4' : ''
            }`}
          />
        </div>
      </div>
      <div>
        <p className='text-sm text-gray-200'>{label}</p>
        {desc && <p className='text-xs text-gray-500 mt-0.5'>{desc}</p>}
      </div>
    </label>
  );
}

// ── Proxy Server Picker — kde se host vytvoří ───────────────────────────────

function ProxyServerPicker() {
  const { t } = useTranslation();
  const { data: instResp } = useQuery<{ ok: boolean; instances: NpmInstance[] }>({
    queryKey: ['npm-instances'],
    queryFn: () => callMcp('npm.instances_list', {}),
    staleTime: 30_000,
  });

  // Po sjednocení 2026-05-14 (smazaný Mode A/B koncept): místo `mode` ukážeme
  // počet `internal` vs `edge` instancí — to je co operatora reálně zajímá.
  const instances = instResp?.instances ?? [];
  const local = instances.find((i) =>
    (i as { role?: string }).role === 'internal' || !i.is_upstream
  );
  const upstream = instances.filter((i) =>
    (i as { role?: string }).role === 'edge' || i.is_upstream
  );

  return (
    <div className='mb-4 pb-3 border-b border-gray-800/30'>
      <label className='text-xs text-gray-500 mb-2 block font-medium'>
        {t('npm.serverKdeSeProxy')}
      </label>
      <div className='space-y-1.5'>
        {/* Lokální nginx */}
        {local && (
          <div className='flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-blue-500/30 bg-blue-500/5'>
            <div className='w-2 h-2 rounded-full bg-blue-500' />
            <div className='flex-1'>
              <span className='text-sm text-white font-medium'>
                {local.label || t('npm.localNginx', 'Lokální nginx')}
              </span>
              <span className='text-[10px] text-gray-500 ml-2'>{(local as { role?: string })?.role === 'edge' ? t('npm.edgeLabel') : t('npm.internalLabel')}</span>
            </div>
            <span className='text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400'>
              {t('npm.aktivni3')}
            </span>
          </div>
        )}
        {/* Nadřazené (edge/upstream) — plnohodnotné řádky, ne mikro-poznámka.
            Filip 2026-09-02: edge nebyla vidět a nešla vybrat. */}
        {upstream.map((u) => (
          <div
            key={u.id}
            className='flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-amber-500/30 bg-amber-500/5'
          >
            <div className='w-2 h-2 rounded-full bg-amber-400' />
            <div className='flex-1'>
              <span className='text-sm text-white font-medium'>{u.label || u.id}</span>
              <span className='text-[10px] text-gray-500 ml-2'>
                {(u as { role?: string }).role === 'edge'
                  ? t('npm.edgeLabel', 'edge (veřejný vstup)')
                  : t('npm.upstreamLabel', 'nadřazená proxy')}
              </span>
            </div>
            <span className='text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400'>
              {t('npm.pouzijeSePriZpristupnit')}
            </span>
          </div>
        ))}
        {/* Inzerované edge v mesh, které tento nod ještě nepřijal — jeden
            tenant, spoky přes ně inzerují (npm.edge_use). */}
        <AdvertisedEdges
          advertised={(instResp as { advertised_edges?: AdvertisedEdge[] } | undefined)
            ?.advertised_edges ?? []}
        />
      </div>
    </div>
  );
}

type AdvertisedEdge = {
  key: string;
  stable_id: string;
  hostname: string;
  public_ip?: string | null;
  adopted?: boolean;
};

function AdvertisedEdges({ advertised }: { advertised: AdvertisedEdge[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const pending = advertised.filter((e) => !e.adopted);
  const adopt = useMutation({
    mutationFn: async (stableId: string) => {
      const r = await callMcp('npm.edge_use', {
        hostname: stableId,
        set_default: true,
      }) as { ok?: boolean; error?: string };
      if (r && (r as { ok?: boolean }).ok === false) {
        throw new Error((r as { error?: string }).error ?? 'Adopce edge selhala');
      }
      return r;
    },
    onSuccess: () => {
      toast.success(t('npm.edgeAdopted', 'Edge přijata jako nadřazená proxy'));
      qc.invalidateQueries({ queryKey: ['npm-instances'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  if (!pending.length) return null;
  return (
    <div className='space-y-1.5'>
      {pending.map((e) => (
        <div
          key={e.key}
          className='flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-700 bg-gray-900/40'
        >
          <Globe size={12} className='text-gray-500' />
          <div className='flex-1'>
            <span className='text-sm text-gray-300'>{e.hostname}</span>
            <span className='text-[10px] text-gray-500 ml-2'>
              {t('npm.edgeAdvertised', 'edge v mesh')}
              {e.public_ip ? ` · ${e.public_ip}` : ''}
            </span>
          </div>
          <button
            type='button'
            disabled={adopt.isPending}
            onClick={() => adopt.mutate(e.stable_id)}
            className='text-[10px] px-2 py-1 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 transition-colors'
          >
            {adopt.isPending
              ? t('npm.edgeAdopting', 'Přijímám…')
              : t('npm.edgeAdopt', 'Použít jako nadřazenou')}
          </button>
        </div>
      ))}
    </div>
  );
}

// ── HA Target Picker — výběr backup cílů ze známých služeb ───────────────────

function HaTargetPicker({ targets, onChange }: {
  targets: HaTarget[];
  onChange: (targets: HaTarget[]) => void;
}) {
  const { t } = useTranslation();
  const [showPicker, setShowPicker] = useState(false);
  const { data } = useQuery<{ ok: boolean; targets: ProxyTarget[]; total: number }>({
    queryKey: ['npm-proxy-targets'],
    queryFn: () =>
      callMcp('npm.proxy_targets', {}),
    staleTime: 15_000,
    enabled: showPicker,
  });
  const available = data?.targets ?? [];

  const addTarget = (t: { host: string; port: number }) => {
    // Skip if already added
    if (targets.some((e) => e.host === t.host && e.port === String(t.port))) return;
    onChange([...targets, { host: t.host, port: String(t.port), backup: true }]);
  };

  return (
    <div className='border-t border-gray-800/30 pt-4'>
      <div className='flex items-center justify-between mb-2'>
        <label className='text-xs text-gray-400 font-medium'>{t('npm.haFailoverCile')}</label>
        <button
          type='button'
          onClick={() => setShowPicker(!showPicker)}
          className='text-[10px] text-blue-400 hover:text-blue-300'
        >
          {showPicker ? t('npm.skrytVyber') : t('npm.pridatZeSluzeb')}
        </button>
      </div>

      {targets.length === 0 && !showPicker && (
        <p className='text-[10px] text-gray-600'>
          {t('npm.zadneBackupCileText')}
        </p>
      )}

      {/* Vybrané HA targets */}
      {targets.length > 0 && (
        <div className='space-y-1.5 mb-3'>
          {targets.map((target, i) => (
            <div
              key={i}
              className='flex items-center gap-2 px-2.5 py-2 rounded-lg bg-gray-800/30 border border-gray-800'
            >
              <span className='w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0' />
              <span className='text-xs font-mono text-white flex-1'>{target.host}:{target.port}</span>
              <span
                className={`text-[9px] px-1.5 py-0.5 rounded ${
                  target.backup ? 'bg-amber-500/10 text-amber-400' : 'bg-blue-500/10 text-blue-400'
                }`}
              >
                {target.backup ? t('npm.backupLabel') : t('npm.activeLabel')}
              </span>
              <button
                type='button'
                onClick={() => {
                  const next = [...targets];
                  next[i] = { ...target, backup: !target.backup };
                  onChange(next);
                }}
                className='text-[9px] text-gray-500 hover:text-white'
              >
                flip
              </button>
              <button
                type='button'
                onClick={() => {
                  const next = [...targets];
                  next.splice(i, 1);
                  onChange(next);
                }}
                className='text-gray-600 hover:text-red-400 p-0.5'
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
          <p className='text-[9px] text-gray-600'>
            {t('npm.nginxUpstreamMaxFails')}
          </p>
        </div>
      )}

      {/* Picker ze služeb */}
      {showPicker && (
        <div className='border border-gray-800 rounded-lg bg-gray-950/50 max-h-48 overflow-y-auto'>
          {available.filter((svc) => svc.type === 'service' || svc.type === 'docker').map((svc) => {
            const alreadyAdded = targets.some((e) =>
              e.host === svc.host && e.port === String(svc.port)
            );
            return (
              <button
                key={svc.id}
                type='button'
                disabled={alreadyAdded}
                onClick={() => addTarget(svc)}
                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${
                  alreadyAdded ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-800'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    svc.type === 'service' ? 'bg-green-500' : 'bg-blue-500'
                  }`}
                />
                <span className='text-white font-mono'>{svc.host}:{svc.port}</span>
                <span className='text-gray-500 truncate'>{svc.label}</span>
                {alreadyAdded && <span className='text-[9px] text-gray-600 ml-auto'>{t('npm.pridano')}</span>}
              </button>
            );
          })}
          {available.length === 0 && (
            <p className='text-xs text-gray-600 p-3 text-center'>{t('npm.zadneSluzbyKDispozici')}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Target picker — výběr proxy cíle ze seznamu Docker/služeb ────────────────

function TargetPicker(
  { onSelect }: { onSelect: (target: { host: string; port: number; label: string }) => void },
) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data } = useQuery<{ ok: boolean; targets: ProxyTarget[]; total: number }>({
    queryKey: ['npm-proxy-targets'],
    queryFn: () =>
      callMcp('npm.proxy_targets', {}),
    staleTime: 15_000,
    enabled: open,
  });
  const targets = data?.targets ?? [];
  const services = targets.filter((tgt) => tgt.type === 'service');
  const docker = targets.filter((tgt) => tgt.type === 'docker');
  const localHosts = targets.filter((tgt) => tgt.type === 'local-host');

  return (
    <div className='mb-3'>
      <button
        type='button'
        onClick={() => setOpen(!open)}
        className='text-xs text-blue-400 hover:text-blue-300 mb-1'
      >
        {open ? t('npm.skrytVyberCile') : t('npm.vybratCilZeSeznamu')}
      </button>
      {open && (
        <div className='border border-gray-800 rounded-lg bg-gray-950/50 max-h-48 overflow-y-auto'>
          {services.length > 0 && (
            <div className='px-2 pt-2'>
              <p className='text-[9px] text-gray-500 uppercase tracking-wider mb-1'>{t('npm.acmSluzby')}</p>
              {services.map((tgt) => (
                <button
                  key={tgt.id}
                  type='button'
                  onClick={() => {
                    onSelect(tgt);
                    setOpen(false);
                  }}
                  className='w-full text-left px-2 py-1.5 rounded text-xs hover:bg-gray-800 flex items-center gap-2'
                >
                  <span className='w-1.5 h-1.5 rounded-full bg-green-500' />
                  <span className='text-white font-mono'>{tgt.host}:{tgt.port}</span>
                  <span className='text-gray-500 truncate'>{tgt.label}</span>
                </button>
              ))}
            </div>
          )}
          {docker.length > 0 && (
            <div className='px-2 pt-2'>
              <p className='text-[9px] text-gray-500 uppercase tracking-wider mb-1'>
                {t('npm.dockerKontejnery')}
              </p>
              {docker.map((tgt) => (
                <button
                  key={tgt.id}
                  type='button'
                  onClick={() => {
                    onSelect(tgt);
                    setOpen(false);
                  }}
                  className='w-full text-left px-2 py-1.5 rounded text-xs hover:bg-gray-800 flex items-center gap-2'
                >
                  <span className='w-1.5 h-1.5 rounded-full bg-blue-500' />
                  <span className='text-white font-mono'>{tgt.host}:{tgt.port}</span>
                  <span className='text-gray-500 truncate'>{tgt.label}</span>
                </button>
              ))}
            </div>
          )}
          {localHosts.length > 0 && (
            <div className='px-2 pt-2 pb-2'>
              <p className='text-[9px] text-gray-500 uppercase tracking-wider mb-1'>
                {t('npm.lokalniProxyRetezeni')}
              </p>
              {localHosts.map((tgt) => (
                <button
                  key={tgt.id}
                  type='button'
                  onClick={() => {
                    onSelect(tgt);
                    setOpen(false);
                  }}
                  className='w-full text-left px-2 py-1.5 rounded text-xs hover:bg-gray-800 flex items-center gap-2'
                >
                  <span className='w-1.5 h-1.5 rounded-full bg-amber-500' />
                  <span className='text-white font-mono'>{tgt.host}:{tgt.port}</span>
                  <span className='text-gray-500 truncate'>{tgt.label}</span>
                </button>
              ))}
            </div>
          )}
          {targets.length === 0 && (
            <p className='text-xs text-gray-600 p-3 text-center'>{t('npm.zadneCileNalezeny')}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Template selector — dropdown pro předvyplnění host formuláře ──────────────

function TemplateSelector({ onApply }: { onApply: (tpl: ProxyTemplate) => void }) {
  const { t } = useTranslation();
  const { data } = useQuery<{ ok: boolean; templates: ProxyTemplate[] }>({
    queryKey: ['npm-templates'],
    queryFn: () => callMcp('npm.templates_list', {}),
    staleTime: 60_000,
  });
  const templates = data?.templates ?? [];
  if (templates.length === 0) return null;

  return (
    <div className='mb-4 pb-3 border-b border-gray-800/30'>
      <label className='text-xs text-gray-400 mb-1.5 block'>
        {t('npm.sablonaVolitelnaPredvyplniNast')}
      </label>
      <Select
        defaultValue=''
        onChange={(e) => {
          const tpl = templates.find((tp) => `${tp.module}/${tp.key}` === e.target.value);
          if (tpl) onApply(tpl);
        }}
      >
        <option value=''>{t('npm.bezSablony')}</option>
        {templates.map((tp) => (
          <option key={`${tp.module}/${tp.key}`} value={`${tp.module}/${tp.key}`}>
            {String(tp.label)} ({tp.module}/{String(tp.key)})
          </option>
        ))}
      </Select>
    </div>
  );
}

// ── Host modal (dvě záložky — portováno z PCC) ────────────────────────────────

function HostModal({ title, initial, certs, onClose, onSubmit, instanceSelector }: {
  title: string;
  initial: HostForm;
  certs: Certificate[];
  onClose: () => void;
  onSubmit: (form: HostForm) => Promise<void>;
  instanceSelector?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<HostForm>(initial);
  const [tab, setTab] = useState<'basic' | 'advanced'>('basic');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof HostForm, string>>>({});

  const set = <K extends keyof HostForm>(k: K, v: HostForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e: typeof errors = {};
    if (!form.domain.trim()) e.domain = t('npm.domainRequired', 'Doména je povinná');
    else if (!/^[a-z0-9*][a-z0-9.*-]*\.[a-z]{2,}$/.test(form.domain.trim())) {
      e.domain = t('npm.domainInvalid', 'Neplatný formát domény (např. app.services.example.cz)');
    }
    if (!form.forward_host.trim()) e.forward_host = t('npm.hostRequired', 'Host je povinný');
    if (!form.forward_port || isNaN(parseInt(form.forward_port))) e.forward_port = t('npm.mustBeNumber', 'Musí být číslo');
    return e;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setLoading(true);
    try {
      await onSubmit(form);
    } finally {
      setLoading(false);
    }
  };

  const errCls = (k: keyof HostForm) =>
    `${inputCls} ${errors[k] ? 'border-red-500/70 focus:border-red-500' : ''}`;

  // Portál do body: HostModal je uvnitř widget karty s backdrop-filter → fixed
  // by jinak seděl jen v kartě (nalepené na krajích, oříznuté).
  return createPortal(
    <div
      className='fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/60 backdrop-blur-sm p-4 sm:p-6'
      onClick={onClose}
      role='presentation'
    >
      <div
        role='dialog'
        aria-modal='true'
        className='bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[min(90vh,calc(100dvh-2rem))] my-auto'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='px-5 sm:px-6 pt-5 sm:pt-6 pb-0 shrink-0'>
          <div className='flex items-center justify-between mb-4 gap-3'>
            <h2 className='text-lg font-semibold text-white truncate'>{title}</h2>
            <button
              type='button'
              onClick={onClose}
              className='p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 shrink-0'
            >
              <X size={16} />
            </button>
          </div>
          {/* Tabs */}
          <div className='flex gap-1 bg-gray-800 rounded-lg p-1 mb-4'>
            {(['basic', 'advanced'] as const).map((tabKey) => (
              <button
                key={tabKey}
                type='button'
                onClick={() => setTab(tabKey)}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  tab === tabKey ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {tabKey === 'basic' ? t('npm.zakladni') : t('npm.pokrocile')}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className='flex flex-col flex-1 min-h-0'>
          <div className='px-5 sm:px-6 overflow-y-auto flex-1 min-h-0'>
            {instanceSelector}
            {/* Server / provider selector */}
            <ProxyServerPicker />
            {/* Target picker — výběr cíle ze seznamu */}
            <TargetPicker
              onSelect={(target) => {
                set('forward_host', target.host);
                set('forward_port', String(target.port));
              }}
            />
            {/* Šablona selector */}
            <TemplateSelector
              onApply={(tpl) => {
                if (tpl.ssl !== undefined) set('ssl_forced', tpl.ssl);
                if (tpl.websockets !== undefined) {
                  set('allow_websocket_upgrade', tpl.websockets);
                }
                if (tpl.http2 !== undefined) set('http2_support', tpl.http2);
                if (tpl.hsts_enabled !== undefined) set('hsts_enabled', tpl.hsts_enabled);
                if (tpl.block_exploits !== undefined) set('block_exploits', tpl.block_exploits);
                if (tpl.forward_scheme) set('forward_scheme', tpl.forward_scheme);
                if (tpl.advanced_config) set('advanced_config', tpl.advanced_config);
              }}
            />
            {/* Základní záložka */}
            {tab === 'basic' && (
              <div className='space-y-4 pb-4'>
                <div>
                  <label className='text-xs text-gray-400 mb-1 block'>{t('npm.domena')}</label>
                  <input
                    className={`${errCls('domain')} font-mono`}
                    placeholder='app.services.maxcloud.cz'
                    value={form.domain}
                    onChange={(e) => set('domain', e.target.value)}
                    autoFocus
                  />
                  {errors.domain && <p className='text-red-400 text-xs mt-1'>{errors.domain}</p>}
                </div>

                <div className='grid grid-cols-3 gap-3'>
                  <div>
                    <label className='text-xs text-gray-400 mb-1 block'>{t('npm.schema')}</label>
                    <Select
                      value={form.forward_scheme}
                      onChange={(e) => set('forward_scheme', e.target.value)}
                    >
                      <option value='http'>HTTP</option>
                      <option value='https'>HTTPS</option>
                    </Select>
                  </div>
                  <div className='col-span-2'>
                    <label className='text-xs text-gray-400 mb-1 block'>{t('npm.cilovyHost')}</label>
                    <input
                      className={`${errCls('forward_host')} font-mono`}
                      placeholder='10.119.251.11'
                      value={form.forward_host}
                      onChange={(e) => set('forward_host', e.target.value)}
                    />
                    {errors.forward_host && (
                      <p className='text-red-400 text-xs mt-1'>{errors.forward_host}</p>
                    )}
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <label className='text-xs text-gray-400 mb-1 block'>{t('npm.port')}</label>
                    <input
                      type='number'
                      className={errCls('forward_port')}
                      value={form.forward_port}
                      onChange={(e) => set('forward_port', e.target.value)}
                    />
                    {errors.forward_port && (
                      <p className='text-red-400 text-xs mt-1'>{errors.forward_port}</p>
                    )}
                  </div>
                  <div>
                    <label className='text-xs text-gray-400 mb-1 block'>{t('npm.sslCertifikat')}</label>
                    <Select
                      value={form.certificate_id}
                      onChange={(e) => set('certificate_id', e.target.value)}
                    >
                      <option value='0'>{t('npm.bezCertifikatu')}</option>
                      {certs.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nice_name || c.domain_names[0]}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>

                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='checkbox'
                    className='w-4 h-4 rounded accent-blue-500'
                    checked={form.ssl_forced}
                    onChange={(e) => set('ssl_forced', e.target.checked)}
                  />
                  <span className='text-sm text-gray-300'>{t('npm.forceSslHttpHttps')}</span>
                </label>

                {/* Řetězení — zpřístupnit z internetu (vytvořit i na nadřazené proxy) */}
                <div className='mt-3 p-3 rounded-xl border border-blue-500/20 bg-blue-500/5'>
                  <label className='flex items-center gap-2 cursor-pointer'>
                    <input
                      type='checkbox'
                      className='w-4 h-4 rounded accent-blue-500'
                      checked={form.publish_upstream}
                      onChange={(e) => set('publish_upstream', e.target.checked)}
                    />
                    <div>
                      <span className='text-sm text-blue-300 font-medium'>
                        {t('npm.zpristupnitZInternetu')}
                      </span>
                      <p className='text-[10px] text-gray-500 mt-0.5'>
                        {t('npm.vytvoriZaznamINaNadrazene')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Pokročilé záložka */}
            {tab === 'advanced' && (
              <div className='space-y-5 pb-4'>
                <div className='space-y-4'>
                  <Toggle
                    label='WebSocket'
                    desc='HTTP → WebSocket upgrade (Socket.IO, ws://)'
                    checked={form.allow_websocket_upgrade}
                    onChange={(v) => set('allow_websocket_upgrade', v)}
                  />
                  <Toggle
                    label={t('npm.blockExploits')}
                    desc={t('npm.blockExploitsDesc', 'Blokuje exploity a podezřelé URL vzory')}
                    checked={form.block_exploits}
                    onChange={(v) => set('block_exploits', v)}
                  />
                  <Toggle
                    label={t('npm.blockBlacklistUtm')}
                    desc='Blokuje IP z UTM blacklistu (geo $acm_blacklisted → 444)'
                    checked={form.block_blacklist}
                    onChange={(v) => set('block_blacklist', v)}
                  />
                  <Toggle
                    label={t('npm.http2Support')}
                    desc={t('npm.http2Desc', 'HTTP/2 pro backend připojení')}
                    checked={form.http2_support}
                    onChange={(v) => set('http2_support', v)}
                  />
                  <Toggle
                    label='HSTS'
                    desc={t('npm.hstsDesc', 'Strict-Transport-Security hlavička')}
                    checked={form.hsts_enabled}
                    onChange={(v) => set('hsts_enabled', v)}
                  />
                </div>
                <div>
                  <label className='text-xs text-gray-400 mb-1 block'>{t('npm.customNginxConfig')}</label>
                  <textarea
                    className={`${inputCls} font-mono text-xs h-32 resize-none`}
                    placeholder={'proxy_read_timeout 300s;\nproxy_buffering off;'}
                    value={form.advanced_config}
                    onChange={(e) => set('advanced_config', e.target.value)}
                    spellCheck={false}
                  />
                  <p className='text-[10px] text-gray-600 mt-1'>
                    {t('npm.vloziSeDoLocationBloku')}{' '}
                    <code className='font-mono'>proxy_http_version 1.1</code>{' '}
                    {t('npm.npmHoPridavaAutomaticky')}
                  </p>
                </div>

                {/* HA — High Availability targets (ze známých služeb) */}
                <HaTargetPicker
                  targets={form.ha_targets}
                  onChange={(t) => set('ha_targets', t)}
                />
              </div>
            )}
          </div>

          <div className='flex gap-3 px-5 sm:px-6 py-4 border-t border-gray-700/50 shrink-0'>
            <button
              type='button'
              onClick={onClose}
              className='flex-1 px-4 py-2 rounded-xl border border-gray-700 text-gray-400 hover:text-white text-sm transition-colors'
            >
              {t('npm.zrusit')}
            </button>
            <button
              type='submit'
              disabled={loading}
              className='flex-1 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2'
            >
              {loading ? t('npm.saving', 'Ukládám…') : (
                <>
                  <Save size={14} /> {t('npm.ulozit')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

// ── Instance picker (sdílený mezi Hosts a Stream) ────────────────────────────

function InstancePicker({ instances, selected, onChange, className }: {
  instances: Array<{ id: string; label: string; count?: number }>;
  selected: string;
  onChange: (id: string) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className={`flex gap-1 flex-wrap ${className ?? ''}`}>
      <button
        type='button'
        onClick={() => onChange('__all__')}
        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
          selected === '__all__'
            ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
            : 'bg-gray-800/60 text-gray-400 border border-gray-800 hover:text-white hover:border-gray-700'
        }`}
      >
        {t('npm.vse')}
      </button>
      {instances.map((inst) => (
        <button
          key={inst.id}
          type='button'
          onClick={() => onChange(inst.id)}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
            selected === inst.id
              ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
              : 'bg-gray-800/60 text-gray-400 border border-gray-800 hover:text-white hover:border-gray-700'
          }`}
        >
          {inst.label}
          {inst.count !== undefined && (
            <span className='text-[10px] opacity-60'>({inst.count})</span>
          )}
        </button>
      ))}
    </div>
  );
}

// ── Hosts tab ─────────────────────────────────────────────────────────────────

function SystemServicesPanel() {
  const { t } = useTranslation();
  const { data } = useQuery<{ ok: boolean; count: number; hosts: ModuleHost[] }>({
    queryKey: ['npm-module-hosts'],
    queryFn: () =>
      callMcp('npm.list_module_hosts', {}),
    staleTime: 30_000,
  });

  const systemHosts = data?.hosts?.filter((h) => h.system) ?? [];
  const moduleHosts = data?.hosts?.filter((h) => !h.system) ?? [];

  if (systemHosts.length === 0 && moduleHosts.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Server}>{t('npm.systemoveAModuloveSluzby')}</CardTitle>
      </CardHeader>
      <CardContent className='p-0'>
        {systemHosts.length > 0 && (
          <div className='divide-y divide-gray-800/30'>
            {systemHosts.map((h) => (
              <div key={h.service_name} className='px-4 py-2.5 flex items-center gap-3 opacity-70'>
                <div
                  className={`w-2 h-2 rounded-full ${h.alive ? 'bg-green-500' : 'bg-gray-600'}`}
                />
                <span className='text-sm text-gray-400 font-medium w-20'>{h.service_name}</span>
                <span className='text-[10px] font-mono text-gray-600'>
                  :{h.internal_port} → :{h.external_port}
                </span>
                <span className='text-[9px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-500 font-medium uppercase'>
                  system
                </span>
                <span className='ml-auto text-[10px] text-gray-600'>{h.instance}</span>
              </div>
            ))}
          </div>
        )}
        {moduleHosts.length > 0 && (
          <>
            {systemHosts.length > 0 && <div className='border-t border-gray-800/50' />}
            <div className='divide-y divide-gray-800/30'>
              {moduleHosts.map((h, i) => (
                <div key={i} className='px-4 py-2.5 flex items-center gap-3'>
                  <div className='w-2 h-2 rounded-full bg-blue-500' />
                  <span className='text-sm text-white font-medium'>{h.domain}</span>
                  <span className='text-[10px] text-gray-600'>
                    {h.module}
                    {h.instance ? ` / ${h.instance}` : ''}
                  </span>
                  {h.host_id && (
                    <span className='text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-mono'>
                      NPM #{h.host_id}
                    </span>
                  )}
                  <span className='ml-auto text-[10px] text-gray-500'>{h.source}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function HostsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editHost, setEditHost] = useState<(ProxyHost & { _instance_id?: string }) | null>(null);
  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState<string | null>(null);
  const [selectedInstance, setSelectedInstance] = useState('__all__');

  // Načíst instance pro picker
  const { data: instResp } = useQuery<{ ok: boolean; instances: NpmInstance[] }>({
    queryKey: ['npm-instances'],
    queryFn: () => callMcp('npm.instances_list', {}),
  });
  const npmInstances = (instResp?.instances ?? []).filter((i) => i.is_upstream);
  const defaultInstance = npmInstances.find((i) => i.is_default)?.id ?? npmInstances[0]?.id ?? '';

  // Orphan proxy záznamy z registru
  const { data: orphanResp } = useQuery<
    { ok: boolean; orphans: Array<{ domain: string; source: string; reason: string }> }
  >({
    queryKey: ['npm-orphans'],
    queryFn: () => callMcp('npm.orphans_list', {}),
    staleTime: 60_000,
  });
  const orphanDomains = new Set((orphanResp?.orphans ?? []).map((o) => o.domain));

  const deleteOrphan = useMutation({
    mutationFn: (domain: string) => callMcp('npm.orphan_delete', { domain }),
    onSuccess: () => {
      toast.success(t('npm.orphanSmazan'));
      qc.invalidateQueries({ queryKey: ['npm-orphans'] });
      qc.invalidateQueries({ queryKey: ['npm-hosts-all'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  // Agregované hosty ze všech instancí
  const { data: allResp, isLoading, isFetching, refetch } = useQuery<
    {
      ok: boolean;
      instances: Array<
        { instance_id: string; instance_label: string; hosts: ProxyHost[]; error?: string }
      >;
    }
  >({
    queryKey: ['npm-hosts-all'],
    queryFn: () => callMcp('npm.hosts_all', {}),
    refetchInterval: 60_000,
  });

  const { data: certsResp } = useQuery<{ certs: Certificate[] } | Certificate[]>({
    queryKey: ['npm-certs'],
    queryFn: () => callMcp('npm.list_certs', {}),
    staleTime: 120_000,
  });

  // Sloučit hosty se zdrojovou instancí
  const allHosts: Array<ProxyHost & { _instance_id: string; _instance_label: string }> = [];
  for (const inst of allResp?.instances ?? []) {
    for (const h of inst.hosts) {
      allHosts.push({ ...h, _instance_id: inst.instance_id, _instance_label: inst.instance_label });
    }
  }

  const hosts = selectedInstance === '__all__'
    ? allHosts
    : allHosts.filter((h) => h._instance_id === selectedInstance);

  const certs: Certificate[] = Array.isArray(certsResp)
    ? certsResp
    : (certsResp as { certs?: Certificate[] })?.certs ?? [];

  const instancePicks = npmInstances.map((i) => ({
    id: i.id,
    label: i.label ?? i.id,
    count: allHosts.filter((h) => h._instance_id === i.id).length,
  }));

  const invalidate = () => qc.invalidateQueries({ queryKey: ['npm-hosts-all'] });

  const toggle = useMutation({
    mutationFn: ({ id, enabled, instance }: { id: number; enabled: boolean; instance?: string }) =>
      callMcp(enabled ? 'npm.disable_host' : 'npm.enable_host', { id, instance }),
    onSuccess: (_d, vars) => {
      toast.success(vars.enabled ? t('npm.deaktivovano') : t('npm.aktivovano'));
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: ({ id, instance }: { id: number; instance?: string }) =>
      callMcp('npm.delete_host', { id, instance }),
    onSuccess: () => {
      toast.success(t('npm.hostSmazan'));
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [createInstance, setCreateInstance] = useState('');

  const create = useMutation({
    mutationFn: ({ form, instance }: { form: HostForm; instance?: string }) =>
      callMcp('npm.create_host', { ...formToToolArgs(form), instance }),
    onSuccess: () => {
      toast.success(t('npm.hostVytvoren'));
      setShowCreate(false);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const update = useMutation({
    mutationFn: ({ id, form, instance }: { id: number; form: HostForm; instance?: string }) =>
      callMcp('npm.update_host', { ...formToUpdateToolArgs(form), id, instance }),
    onSuccess: () => {
      toast.success(t('npm.ulozeno'));
      setEditHost(null);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const enabled = hosts.filter((h) => h.enabled).length;
  const allDomains = hosts.flatMap((h) => h.domain_names);

  const q = search.trim().toLowerCase();
  const filtered = hosts.filter((h) => {
    if (!matchesDomainFilter(h.domain_names, domainFilter)) return false;
    if (!q) return true;
    return h.domain_names.some((d) => d.toLowerCase().includes(q)) ||
      h.forward_host.toLowerCase().includes(q) ||
      String(h.forward_port).includes(q);
  });

  return (
    <div className='space-y-5'>
      <InstancePicker
        instances={instancePicks}
        selected={selectedInstance}
        onChange={setSelectedInstance}
      />
      <StatsGrid columns={3}>
        <StatCard
          icon={Globe}
          label={t('npm.celkemHostu')}
          value={hosts.length}
          sub={t('npm.proxyZaznamy')}
          variant='info'
        />
        <StatCard
          icon={CheckCircle2}
          label={t('npm.aktivni')}
          value={enabled}
          sub={t('npm.povolene')}
          variant='success'
        />
        <StatCard
          icon={XCircle}
          label={t('npm.zakazane')}
          value={hosts.length - enabled}
          sub='disabled'
          variant='warning'
        />
      </StatsGrid>

      {/* Systémové služby — interní ACM proxy mapování */}
      <SystemServicesPanel />

      {/* Proxy hosté z NPM */}
      <Card>
        <CardHeader>
          <CardTitle icon={Globe}>{t('npm.proxyHoste')}</CardTitle>
          <div className='flex items-center gap-2'>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('npm.hledatDomenuHost')}
              className='w-48'
              resultCount={q ? filtered.length : undefined}
              totalCount={q ? hosts.length : undefined}
            />
            <RefreshButton onRefresh={refetch} loading={isFetching} />
            <Button variant='primary' size='sm' onClick={() => setShowCreate(true)}>
              <Plus size={13} /> {t('npm.novy')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Domain filter */}
          {!isLoading && hosts.length > 0 && (
            <DomainFilter
              domains={allDomains}
              selected={domainFilter}
              onChange={setDomainFilter}
              className='mb-4 pb-4 border-b border-gray-800/60'
            />
          )}

          {isLoading
            ? <div className='text-center py-10 text-sm text-gray-500'>{t('npm.nacitam')}</div>
            : hosts.length === 0
            ? (
              <div className='text-center py-10'>
                <Globe size={32} className='text-gray-700 mx-auto mb-3' />
                <p className='text-sm text-gray-400'>{t('npm.zadneProxyHosty')}</p>
                <p className='text-xs text-gray-600 mt-1'>{t('npm.pridejteDomenuProSmerovani')}</p>
              </div>
            )
            : filtered.length === 0
            ? (
              <div className='text-center py-8 text-sm text-gray-500'>
                {q
                  ? (
                    <>
                      {t('npm.nicPro')} <span className='text-white font-mono'>"{search}"</span>
                    </>
                  )
                  : (
                    <>
                      {t('npm.zadneHostyPod', 'Žádné hosty pod')}{' '}
                      <span className='text-blue-400 font-mono'>{domainFilter}</span>
                    </>
                  )}
              </div>
            )
            : (
              <div className='space-y-2'>
                {(q || domainFilter) && (
                  <p className='text-xs text-gray-600 pb-1'>
                    {t('npm.filtrovanoZHostu', { filtered: filtered.length, total: hosts.length })}
                  </p>
                )}
                {filtered.map((host) => {
                  const domain = host.domain_names[0];
                  const target =
                    `${host.forward_scheme}://${host.forward_host}:${host.forward_port}`;
                  const toggling = toggle.isPending &&
                    (toggle.variables as { id: number } | undefined)?.id === host.id;

                  const managed = Boolean(host.managed_by);
                  return (
                    <div
                      key={host.id}
                      className={`flex items-center gap-3 border rounded-xl px-4 py-3 group transition-all ${
                        managed
                          ? 'bg-gray-900/50 border-gray-800/60 opacity-70'
                          : host.enabled
                          ? 'bg-gray-800/60 border-gray-700/50'
                          : 'bg-gray-900/40 border-gray-800/50 opacity-60'
                      }`}
                    >
                      {/* Power toggle — platformní hosty řídí jejich modul, ne Proxy stránka */}
                      <button
                        onClick={() =>
                          !managed && toggle.mutate({
                            id: host.id,
                            enabled: host.enabled,
                            instance: host._instance_id,
                          })}
                        disabled={toggling || managed}
                        title={managed
                          ? t('npm.spravujePlatforma', 'Spravuje platforma')
                          : host.enabled
                          ? t('npm.deaktivovat')
                          : t('npm.aktivovat')}
                        className={`shrink-0 transition-colors ${
                          managed
                            ? 'text-gray-600 cursor-not-allowed'
                            : host.enabled
                            ? 'text-emerald-400 hover:text-red-400'
                            : 'text-gray-600 hover:text-emerald-400'
                        } ${toggling ? 'animate-pulse' : ''}`}
                      >
                        {host.enabled ? <Power size={15} /> : <PowerOff size={15} />}
                      </button>

                      <div className='w-7 h-7 rounded-lg bg-gray-700/50 flex items-center justify-center shrink-0'>
                        <Globe size={13} className='text-gray-400' />
                      </div>

                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-1.5 flex-wrap'>
                          <span className='font-medium text-sm text-white truncate'>{domain}</span>
                          {selectedInstance === '__all__' && npmInstances.length > 1 &&
                            (host as any)._instance_label && (
                            <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-gray-700/80 text-gray-400'>
                              {(host as any)._instance_label}
                            </span>
                          )}
                          {host.ssl_forced
                            ? <Shield size={11} className='text-emerald-400 shrink-0' />
                            : <ShieldOff size={11} className='text-gray-600 shrink-0' />}
                          {host.allow_websocket_upgrade && (
                            <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/10 text-purple-400'>
                              WS
                            </span>
                          )}
                          {host.block_exploits && (
                            <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-400'>
                              SEC
                            </span>
                          )}
                          {host.managed_by && (
                            <span
                              className='text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400'
                              title={t('npm.spravujePlatformaTitle', 'Publikace platformy — měnit přes vlastnický modul')}
                            >
                              {host.managed_by.service}
                              {host.managed_by.instance_ref ? ` · ${host.managed_by.instance_ref}` : ''}
                            </span>
                          )}
                          {host.advanced_config && (
                            <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-gray-700/80 text-gray-400'>
                              ADV
                            </span>
                          )}
                          {orphanDomains.has(domain) && (
                            <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 font-bold'>
                              ORPHAN
                            </span>
                          )}
                        </div>
                        <p className='text-xs text-gray-500 font-mono truncate mt-0.5'>
                          {target}
                          {host.http2_support && <span className='ml-2 text-gray-600'>h2</span>}
                        </p>
                      </div>

                      {/* Actions — platformní hosty se přes Proxy stránku nemění */}
                      <div className='flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                        {!managed && (
                          <button
                            onClick={() => setEditHost(host)}
                            className='p-1.5 text-gray-500 hover:text-gray-200 hover:bg-gray-700 rounded-lg transition-colors'
                            title={t('common.edit', 'Upravit')}
                          >
                            <Pencil size={13} />
                          </button>
                        )}
                        <a
                          href={`https://${domain}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='p-1.5 text-gray-500 hover:text-gray-300 hover:bg-gray-700 rounded-lg transition-colors'
                        >
                          <ExternalLink size={13} />
                        </a>
                        {!managed && (
                        <button
                          onClick={async () => {
                            if (await themedConfirm(`Smazat ${domain}?`)) {
                              del.mutate({ id: host.id, instance: host._instance_id });
                            }
                          }}
                          className='p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors'
                        >
                          <Trash2 size={13} />
                        </button>
                        )}
                        {orphanDomains.has(domain) && (
                          <button
                            onClick={async () => await themedConfirm(
                                t('npm.odebratOrphanConfirm', { domain }),
                              ) && deleteOrphan.mutate(domain)}
                            className='px-2 py-1 text-[9px] font-bold text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg hover:bg-red-500/20'
                          >
                            {t('npm.orphanOdebrat')}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </CardContent>
      </Card>

      {showCreate && (
        <HostModal
          title={t('npm.novyProxyHost')}
          initial={defaultForm()}
          certs={certs}
          onClose={() => {
            setShowCreate(false);
            setCreateInstance('');
          }}
          onSubmit={async (form) => {
            await create.mutateAsync({ form, instance: createInstance || defaultInstance });
            setCreateInstance('');
          }}
          instanceSelector={npmInstances.length > 1
            ? (
              <div className='mb-4'>
                <label className='text-xs text-gray-400 mb-1 block'>{t('npm.cilovaProxy')}</label>
                <Select
                  value={createInstance || defaultInstance}
                  onChange={(e) => setCreateInstance(e.target.value)}
                >
                  {npmInstances.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.label}
                      {i.is_default ? t('npm.vychoziParens') : ''}
                    </option>
                  ))}
                </Select>
              </div>
            )
            : undefined}
        />
      )}
      {editHost && (
        <HostModal
          title={`Upravit: ${editHost.domain_names[0]}`}
          initial={hostToForm(editHost)}
          certs={certs}
          onClose={() => setEditHost(null)}
          onSubmit={async (form) => {
            await update.mutateAsync({ id: editHost.id, form, instance: editHost._instance_id });
          }}
        />
      )}
    </div>
  );
}

// ── Certs tab ─────────────────────────────────────────────────────────────────

function CertsTab() {
  const { t } = useTranslation();
  // NPM Certifikáty — view-only seznam z jc21 NPM containeru (cross-reference
  // s proxy hosty pro in_use info). Lifecycle (import, generate, scan, renew)
  // patří do Cert Manager modulu — odkaz dole.
  const { data: npmResp, isLoading, refetch } = useQuery<{
    ok: boolean;
    certs?: Array<Certificate & { in_use: boolean; used_by: string[]; usage_count: number }>;
    in_use_count?: number;
    unused_count?: number;
  }>({
    queryKey: ['npm-certs-usage'],
    queryFn: () => callMcp('npm.certs_usage', {}),
  });
  const npmCerts = npmResp?.certs ?? [];
  const inUseCount = npmResp?.in_use_count ?? 0;
  const unusedCount = npmResp?.unused_count ?? 0;

  // Cleanup unused — NPM-specifická akce (NPM má vlastní cert úložiště mimo ACM Cert Store)
  const [cleanupPreview, setCleanupPreview] = useState<Array<{
    id: number;
    nice_name: string;
    domain_names: string[];
  }> | null>(null);
  const cleanupMut = useMutation({
    mutationFn: (dry_run: boolean) =>
      callMcp<{
        ok: boolean;
        dry_run: boolean;
        total_unused: number;
        deleted: number;
        failed: number;
        results: Array<{
          id: number;
          nice_name: string;
          domain_names: string[];
          status: 'deleted' | 'skipped_in_use' | 'failed';
          error?: string;
          used_by?: string[];
        }>;
      }>('npm.cleanup_unused_certs', { dry_run }),
    onSuccess: (r, dry_run) => {
      if (dry_run) {
        const list = (r.results ?? []).map((x) => ({
          id: x.id,
          nice_name: x.nice_name,
          domain_names: x.domain_names,
        }));
        setCleanupPreview(list);
        if (list.length === 0) toast.info(t('npm.zadneNepouziteCertyKe'));
      } else {
        toast.success(t('npm.cleanupResult', 'Smazáno {{deleted}}, selhalo {{failed}}.', {
          deleted: r.deleted,
          failed: r.failed,
        }));
        setCleanupPreview(null);
        refetch();
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function certExpiry(expires: string | null) {
    if (!expires) return { label: 'N/A', cls: 'text-gray-500 bg-gray-800' };
    const days = Math.ceil((new Date(expires).getTime() - Date.now()) / 86400000);
    if (days < 0) return { label: t('npm.expired', 'Expirovaný'), cls: 'text-red-400 bg-red-500/10' };
    if (days < 14) return { label: `${days}d`, cls: 'text-amber-400 bg-amber-500/10' };
    return { label: `${days}d`, cls: 'text-emerald-400 bg-emerald-500/10' };
  }

  const PROVIDER_CLR: Record<string, string> = {
    'lets-encrypt': 'text-emerald-400 bg-emerald-500/10',
    'root-ca': 'text-blue-400 bg-blue-500/10',
    'imported': 'text-purple-400 bg-purple-500/10',
    'npm-managed': 'text-amber-400 bg-amber-500/10',
    'letsencrypt': 'text-emerald-400 bg-emerald-500/10',
  };

  return (
    <div className='space-y-4'>
      {/* Info banner — kde lifecycle */}
      <div className='flex items-center justify-between bg-blue-500/5 border border-blue-500/20 rounded-lg px-4 py-2.5 text-sm'>
        <div className='flex items-center gap-2 text-blue-300'>
          <Info size={14} />
          <span>
            {t('npm.vytvareniImportGenerovaniRenew', 'Vytváření / import / generování / renew certifikátů je v')}{' '}
            <a href='/modules/certmgr#store' className='underline hover:text-blue-200'>
              {t('npm.certManagerCertStore')}
            </a>{t('npm.tadyJeViewOnly')}
          </span>
        </div>
      </div>

      {/* NPM Certs s usage info */}
      <Card>
        <CardHeader>
          <CardTitle icon={Globe}>
            NPM Certifikáty ({npmCerts.length})
            <span className='ml-3 text-xs font-normal'>
              <span className='text-emerald-400'>{inUseCount} {t('npm.pouzito2', 'použito')}</span>
              {' · '}
              <span className={unusedCount > 0 ? 'text-amber-400' : 'text-gray-500'}>
                {unusedCount} {t('npm.nepouzito', 'nepoužito')}
              </span>
            </span>
          </CardTitle>
          <div className='flex items-center gap-2'>
            {unusedCount > 0 && (
              <Button
                variant='warning'
                size='sm'
                loading={cleanupMut.isPending && !cleanupPreview}
                onClick={() => cleanupMut.mutate(true)}
                title={t('npm.zobraziSeznamNepouzitychCertu')}
              >
                <Trash2 size={13} /> {t('npm.smazatNepouzite', 'Smazat nepoužité ({{count}})', { count: unusedCount })}
              </Button>
            )}
            <RefreshButton onRefresh={() => refetch()} loading={isLoading} />
          </div>
        </CardHeader>
        <CardContent className='p-0'>
          {npmCerts.length === 0 ? (
            <div className='text-center py-8 text-gray-500'>
              <Globe size={24} className='mx-auto mb-2 opacity-30' />
              <p className='text-sm'>{t('npm.npmNeobsahujeZadneCertifikaty')}</p>
              <p className='text-xs mt-1'>
                {t('npm.importujCertV')} <a href='/modules/certmgr#store' className='underline'>{t('npm.certManager')}</a>{' '}
                {t('npm.klikniTamNpmAby', 'a klikni tam \'→ NPM\' aby se přenesl sem.')}
              </p>
            </div>
          ) : (
            <table className='w-full'>
              <thead>
                <tr className='border-b border-gray-800'>
                  <th className='text-left py-2.5 px-4 text-xs text-gray-400'>{t('npm.nazev')}</th>
                  <th className='text-left py-2.5 px-4 text-xs text-gray-400'>{t('npm.domeny')}</th>
                  <th className='text-left py-2.5 px-4 text-xs text-gray-400'>Provider</th>
                  <th className='text-left py-2.5 px-4 text-xs text-gray-400'>{t('npm.pouzito')}</th>
                  <th className='text-left py-2.5 px-4 text-xs text-gray-400'>Expiruje</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-800/30'>
                {npmCerts.map((c) => {
                  const exp = certExpiry(c.expires_on);
                  return (
                    <tr
                      key={c.id}
                      className={`hover:bg-gray-800/20 ${!c.in_use ? 'opacity-60' : ''}`}
                    >
                      <td className='py-2.5 px-4 text-sm text-white'>{c.nice_name}</td>
                      <td className='py-2.5 px-4'>
                        <span className='text-xs font-mono text-gray-400'>
                          {c.domain_names?.slice(0, 2).join(', ')}
                        </span>
                      </td>
                      <td className='py-2.5 px-4'>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-lg ${
                            PROVIDER_CLR[c.provider] ?? 'bg-gray-800 text-gray-400'
                          }`}
                        >
                          {c.provider}
                        </span>
                      </td>
                      <td className='py-2.5 px-4'>
                        {c.in_use ? (
                          <span
                            className='text-xs px-2 py-0.5 rounded-lg bg-emerald-500/10 text-emerald-400'
                            title={c.used_by.join('\n')}
                          >
                            {c.usage_count}{t('npm.pouzito3', '× použito')}
                          </span>
                        ) : (
                          <span className='text-xs px-2 py-0.5 rounded-lg bg-amber-500/10 text-amber-400'>
                            {t('npm.nepouzito')}
                          </span>
                        )}
                      </td>
                      <td className='py-2.5 px-4'>
                        <span className={`text-xs px-2 py-0.5 rounded-lg ${exp.cls}`}>
                          {exp.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Cleanup confirm modal */}
      <Modal
        open={cleanupPreview !== null}
        onClose={() => setCleanupPreview(null)}
        title={t('npm.smazatNepouziteNpmCertifikaty')}
        footer={
          <>
            <Button variant='secondary' onClick={() => setCleanupPreview(null)}>
              {t('npm.zrusit')}
            </Button>
            <Button
              variant='danger'
              loading={cleanupMut.isPending}
              disabled={!cleanupPreview || cleanupPreview.length === 0}
              onClick={() => cleanupMut.mutate(false)}
            >
              {t('npm.deleteIrreversible', 'Smazat {{count}}× NEVRATNĚ', { count: cleanupPreview?.length ?? 0 })}
            </Button>
          </>
        }
      >
        <div className='space-y-3 text-sm'>
          {cleanupPreview && cleanupPreview.length === 0 ? (
            <p className='text-gray-300'>{t('npm.zadneNepouziteCertyNenalezeny')}</p>
          ) : (
            <>
              <p className='text-gray-300'>
                {t(
                  'npm.unusedCertsHintPrefix',
                  'Tyto certifikáty NEJSOU referenced žádným proxy hostem na NPM. Smazání je',
                )}{' '}
                <strong className='text-red-400'>{t('npm.nevratne')}</strong>
                {t('npm.unusedCertsHintSuffix', ' — soubory na disku + LE accounting se odstraní.')}
              </p>
              <div className='max-h-72 overflow-y-auto bg-gray-900/40 rounded-lg p-2 space-y-1'>
                {cleanupPreview?.map((x) => (
                  <div key={x.id} className='flex items-center gap-2 text-xs'>
                    <span className='text-gray-500 font-mono'>#{x.id}</span>
                    <span className='text-white'>{x.nice_name}</span>
                    <span className='text-gray-400 font-mono'>
                      ({x.domain_names.slice(0, 2).join(', ')})
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

// ── Instance form modal ───────────────────────────────────────────────────────

function InstanceModal({ inst, onClose, onSave, advertised = [], onAdopt, adopting }: {
  inst: NpmInstance | null;
  onClose: () => void;
  onSave: (data: NpmInstance & { password?: string }) => void;
  /** Nalezené v infrastruktuře (inzerce npm_edges) — nabídnout PŘED ručním zadáním. */
  advertised?: AdvertisedEdge[];
  onAdopt?: (stableId: string) => void;
  adopting?: boolean;
}) {
  const { t } = useTranslation();
  const isNew = inst === null;
  const discovered = isNew ? advertised : [];
  const [id, setId] = useState(inst?.id ?? '');
  const [label, setLabel] = useState(inst?.label ?? '');
  const [url, setUrl] = useState(inst?.url ?? 'http://');
  const [email, setEmail] = useState(inst?.email ?? '');
  const [password, setPassword] = useState('');
  const [isUpstream, setIsUpstream] = useState(inst?.is_upstream ?? false);
  const [isDefault, setIsDefault] = useState(inst?.is_default ?? false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !label || !url || !email) return toast.error(t('npm.vyplnVsechnaPovinnaPole'));
    if (isNew && !password) return toast.error(t('npm.hesloJePovinnePri'));
    onSave({
      id,
      label,
      url,
      email,
      ...(password ? { password } : {}),
      is_upstream: isUpstream,
      is_default: isDefault,
    });
  }

  return (
    <Modal
      open={true}
      title={isNew
        ? t('npm.addInstance', 'Přidat NPM instanci')
        : t('npm.editInstance', 'Upravit instanci {{id}}', { id: inst?.id })}
      onClose={onClose}
    >
      {/* Nejdřív co platforma NAŠLA (inzerce edge v mesh) — ruční zadání až pod tím.
          Filip 2026-09-02: „u volby přidat spíš zobrazit ty, co to našlo,
          a teprve pod nimi možnost ručně." */}
      {discovered.length > 0 && (
        <div className='mb-4 space-y-1.5'>
          <p className='text-xs text-gray-400 font-medium'>
            {t('npm.discoveredInInfra', 'Nalezené v infrastruktuře')}
          </p>
          {discovered.map((e) => (
            <div
              key={e.key}
              className='flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/20 bg-amber-500/5'
            >
              <Globe size={12} className='text-amber-400' />
              <div className='flex-1'>
                <span className='text-sm text-gray-200'>{e.hostname}</span>
                <span className='text-[10px] text-gray-500 ml-2'>
                  {t('npm.edgeAdvertised', 'edge v mesh')}
                  {e.public_ip ? ` · ${e.public_ip}` : ''}
                </span>
              </div>
              {e.adopted
                ? (
                  <span className='text-[10px] px-2 py-1 rounded-lg bg-gray-800 text-gray-500'>
                    {t('npm.edgeAlreadyAdopted', 'už převzatá')}
                  </span>
                )
                : (
                  <button
                    type='button'
                    disabled={adopting}
                    onClick={() => onAdopt?.(e.stable_id)}
                    className='text-[10px] px-2 py-1 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 transition-colors'
                  >
                    {adopting
                      ? t('npm.edgeAdopting', 'Přijímám…')
                      : t('npm.edgeTakeFromInfra', 'Převzít z infrastruktury')}
                  </button>
                )}
            </div>
          ))}
          <div className='flex items-center gap-3 pt-2'>
            <div className='flex-1 h-px bg-gray-800' />
            <span className='text-[10px] text-gray-500'>
              {t('npm.orAddManually', 'nebo zadat ručně')}
            </span>
            <div className='flex-1 h-px bg-gray-800' />
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className='space-y-4'>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>{t('npm.idInstance')}</label>
            <input
              value={id}
              onChange={(e) => setId(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              disabled={!isNew}
              required
              placeholder='upstream'
              className='w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm disabled:opacity-50'
            />
          </div>
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>{t('npm.nazev2')}</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              placeholder={t('npm.npm1Upstream')}
              className='w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm'
            />
          </div>
        </div>
        <div>
          <label className='text-xs text-gray-400 mb-1 block'>{t('npm.url')}</label>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            type='url'
            placeholder='https://10.119.251.11:443'
            className='w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm font-mono'
          />
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>{t('npm.email')}</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              type='email'
              placeholder={t('npm.adminExampleCom')}
              className='w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm'
            />
          </div>
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>
              {t('npm.heslo')} {isNew ? '*' : t('npm.prazdneBezeZmeny')}
            </label>
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type='password'
              placeholder={isNew ? '' : '••••••••'}
              className='w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm'
            />
          </div>
        </div>
        <div className='flex gap-6'>
          <label className='flex items-center gap-2 cursor-pointer'>
            <input
              type='checkbox'
              checked={isUpstream}
              onChange={(e) => setIsUpstream(e.target.checked)}
              className='w-4 h-4 rounded accent-blue-500'
            />
            <span className='text-sm text-gray-300'>{t('npm.nadrazenaUpstream')}</span>
          </label>
          <label className='flex items-center gap-2 cursor-pointer'>
            <input
              type='checkbox'
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className='w-4 h-4 rounded accent-amber-500'
            />
            <span className='text-sm text-gray-300'>{t('npm.vychoziInstance')}</span>
          </label>
        </div>
        <div className='flex gap-3 pt-2'>
          <button
            type='button'
            onClick={onClose}
            className='flex-1 py-2 rounded-xl bg-gray-800 text-gray-300 text-sm hover:bg-gray-700 transition-colors'
          >
            {t('npm.zrusit')}
          </button>
          <button
            type='submit'
            className='flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-500 transition-colors flex items-center justify-center gap-2'
          >
            <Save size={14} /> {isNew ? t('npm.pridat') : t('npm.ulozit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Instance row ──────────────────────────────────────────────────────────────

function InstanceRow({ inst, testResult, onEdit, onDelete, onTest, onSetDefault }: {
  inst: NpmInstance;
  testResult?: { ok: boolean; error?: string };
  onEdit: () => void;
  onDelete: () => void;
  onTest: () => void;
  onSetDefault: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className='flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gray-800/40 border border-gray-800 group hover:border-gray-700 transition-colors'>
      <div
        className={`w-2 h-2 rounded-full flex-shrink-0 ${
          testResult === undefined ? 'bg-gray-600' : testResult.ok ? 'bg-emerald-400' : 'bg-red-400'
        }`}
      />
      <div className='flex-1 min-w-0'>
        <div className='flex items-center gap-1.5'>
          <span className='text-sm font-medium text-white'>{inst.label}</span>
          {inst.is_default && (
            <span className='flex items-center gap-0.5 text-xs text-amber-400'>
              <Star size={10} fill='currentColor' /> {t('npm.vychozi')}
            </span>
          )}
          {inst.is_upstream && (
            <span className='text-xs px-1.5 py-0.5 rounded-md bg-blue-500/15 text-blue-400'>
              upstream
            </span>
          )}
        </div>
        <div className='flex items-center gap-1.5 mt-0.5'>
          <Link2 size={10} className='text-gray-600' />
          <span className='text-xs text-gray-500 font-mono'>{inst.url}</span>
          {testResult && !testResult.ok && (
            <span className='text-xs text-red-400 truncate'>{testResult.error}</span>
          )}
        </div>
      </div>
      <div className='flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
        <button
          onClick={onTest}
          title={t('npm.testPripojeni')}
          className='p-1.5 rounded-lg hover:bg-gray-700 text-gray-500 hover:text-blue-400 transition-colors'
        >
          {testResult?.ok ? <Wifi size={13} className='text-emerald-400' /> : <WifiOff size={13} />}
        </button>
        {!inst.is_default && (
          <button
            onClick={onSetDefault}
            title={t('npm.nastavitJakoVychozi')}
            className='p-1.5 rounded-lg hover:bg-amber-900/30 text-gray-500 hover:text-amber-400 transition-colors'
          >
            <Star size={13} />
          </button>
        )}
        <button
          onClick={onEdit}
          title={t('common.edit', 'Upravit')}
          className='p-1.5 rounded-lg hover:bg-blue-900/30 text-gray-500 hover:text-blue-400 transition-colors'
        >
          <Pencil size={13} />
        </button>
        <button
          onClick={onDelete}
          title='Odebrat'
          className='p-1.5 rounded-lg hover:bg-red-900/30 text-gray-500 hover:text-red-400 transition-colors'
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ── Stream tab ────────────────────────────────────────────────────────────────

interface StreamBlock {
  id: number;
  name: string;
  description: string;
  listen_port: number;
  mode: 'sni_passthrough' | 'tls_terminate' | 'plain_tcp';
  backend: string;
  sni_pattern: string;
  cert_path: string;
  key_path: string;
  enabled: boolean;
  conf_exists: boolean;
}

interface StreamStatus {
  nginx_installed: boolean;
  stream_module_loaded: boolean;
  local_nginx_enabled: boolean;
  stream_blocks_count: number;
  nginx_test_ok: boolean;
  nginx_test_out?: string;
}

const MODE_LABEL_KEYS: Record<string, string> = {
  sni_passthrough: 'npm.sniPassthrough',
  tls_terminate: 'npm.tlsTerminate',
  plain_tcp: 'npm.plainTcp',
};

function StreamBlockModal({
  block,
  onClose,
  onSave,
}: {
  block: Partial<StreamBlock> | null;
  onClose: () => void;
  onSave: (data: Partial<StreamBlock>) => void;
}) {
  const { t } = useTranslation();
  const isNew = !block?.id;
  const [name, setName] = useState(block?.name ?? '');
  const [description, setDescription] = useState(block?.description ?? '');
  const [listenPort, setListenPort] = useState(String(block?.listen_port ?? 8883));
  const [mode, setMode] = useState(block?.mode ?? 'sni_passthrough');
  const [backend, setBackend] = useState(block?.backend ?? '');
  const [sniPattern, setSniPattern] = useState(block?.sni_pattern ?? '');
  const [certPath, setCertPath] = useState(block?.cert_path ?? '');
  const [keyPath, setKeyPath] = useState(block?.key_path ?? '');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({
      ...(block?.name ? { name: block.name } : { name }),
      description,
      listen_port: parseInt(listenPort, 10) || 443,
      mode: mode as StreamBlock['mode'],
      backend,
      sni_pattern: sniPattern,
      cert_path: certPath,
      key_path: keyPath,
    });
  }

  const inputCls =
    'w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500';
  const labelCls = 'block text-xs text-gray-400 mb-1';

  return (
    <Modal
      open={true}
      title={isNew
        ? t('npm.newStreamBlock', 'Nový stream blok')
        : t('npm.editStreamBlock', 'Upravit: {{name}}', { name: block?.name })}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className='space-y-4'>
        {isNew && (
          <div>
            <label className={labelCls}>{t('npm.nazevSlug')}</label>
            <input
              className={inputCls}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder='mqtt-tls'
              pattern='[a-z0-9\-]+'
            />
          </div>
        )}
        <div>
          <label className={labelCls}>Popis</label>
          <input
            className={inputCls}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('npm.mqttTlsPassthrough')}
          />
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <label className={labelCls}>{t('npm.listenPort')}</label>
            <input
              className={inputCls}
              type='number'
              value={listenPort}
              onChange={(e) => setListenPort(e.target.value)}
              required
              min={1}
              max={65535}
            />
          </div>
          <div>
            <label className={labelCls}>{t('npm.rezim')}</label>
            <Select
              value={mode}
              onChange={(e) => setMode(e.target.value as StreamBlock['mode'])}
            >
              <option value='sni_passthrough'>{t('npm.sniPassthrough')}</option>
              <option value='tls_terminate'>{t('npm.tlsTerminate')}</option>
              <option value='plain_tcp'>{t('npm.plainTcp')}</option>
            </Select>
          </div>
        </div>
        <div>
          <label className={labelCls}>Backend (host:port) *</label>
          <input
            className={inputCls}
            value={backend}
            onChange={(e) => setBackend(e.target.value)}
            required
            placeholder='localhost:1883'
          />
        </div>
        {mode === 'sni_passthrough' && (
          <div>
            <label className={labelCls}>{t('npm.sniPatternVolitelne')}</label>
            <input
              className={inputCls}
              value={sniPattern}
              onChange={(e) => setSniPattern(e.target.value)}
              placeholder='mqtt.example.com'
            />
          </div>
        )}
        {mode === 'tls_terminate' && (
          <>
            <div>
              <label className={labelCls}>{t('npm.cestaKCertifikatu')}</label>
              <input
                className={inputCls}
                value={certPath}
                onChange={(e) => setCertPath(e.target.value)}
                placeholder='/etc/nginx/certs/mqtt.crt'
              />
            </div>
            <div>
              <label className={labelCls}>{t('npm.cestaKPrivatnimuKlici')}</label>
              <input
                className={inputCls}
                value={keyPath}
                onChange={(e) => setKeyPath(e.target.value)}
                placeholder='/etc/nginx/certs/mqtt.key'
              />
            </div>
          </>
        )}
        <div className='flex justify-end gap-2 pt-2'>
          <Button variant='ghost' type='button' onClick={onClose}>{t('npm.zrusit')}</Button>
          <Button variant='primary' type='submit'>{isNew ? t('npm.vytvorit') : t('npm.ulozit')}</Button>
        </div>
      </form>
    </Modal>
  );
}

function StreamTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editBlock, setEditBlock] = useState<Partial<StreamBlock> | null | 'new'>(null);

  const { data: listData, isLoading, isFetching, refetch } = useQuery<
    { ok: boolean; blocks: StreamBlock[] }
  >({
    queryKey: ['npm-stream-blocks'],
    queryFn: () => callMcp('npm.stream_list', {}),
  });
  const blocks = listData?.blocks ?? [];

  const { data: statusData } = useQuery<StreamStatus>({
    queryKey: ['npm-stream-status'],
    queryFn: () => callMcp('npm.stream_status', { action: 'status' }),
  });

  const create = useMutation({
    mutationFn: (data: Partial<StreamBlock>) =>
      callMcp('npm.stream_create', { action: 'create', ...data }),
    onSuccess: () => {
      toast.success(t('npm.streamBlokVytvoren'));
      setEditBlock(null);
      qc.invalidateQueries({ queryKey: ['npm-stream-blocks'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const update = useMutation({
    mutationFn: ({ name, data }: { name: string; data: Partial<StreamBlock> }) =>
      callMcp('npm.stream_create', { action: 'update', name, ...data }),
    onSuccess: () => {
      toast.success(t('npm.ulozeno'));
      setEditBlock(null);
      qc.invalidateQueries({ queryKey: ['npm-stream-blocks'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (name: string) => callMcp('npm.stream_create', { action: 'delete', name }),
    onSuccess: () => {
      toast.success(t('npm.smazano'));
      qc.invalidateQueries({ queryKey: ['npm-stream-blocks'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = useMutation({
    mutationFn: ({ name, enabled }: { name: string; enabled: boolean }) =>
      callMcp('npm.stream_create', { action: enabled ? 'enable' : 'disable', name }),
    onSuccess: () => {
      toast.success(t('npm.stavZmenen'));
      qc.invalidateQueries({ queryKey: ['npm-stream-blocks'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function handleSave(data: Partial<StreamBlock>) {
    if (editBlock === 'new') {
      create.mutate(data);
    } else if (editBlock?.name) {
      update.mutate({ name: editBlock.name, data });
    }
  }

  async function confirmDelete(name: string) {
    if (await themedConfirm(`Smazat stream blok "${name}"?`)) remove.mutate(name);
  }

  const nginxOk = statusData?.nginx_installed && statusData?.stream_module_loaded;

  return (
    <div className='space-y-4'>
      {/* Nginx status banner */}
      {statusData && (
        <Card>
          <CardContent>
            <div className='flex flex-wrap items-center gap-4 py-1'>
              <div className='flex items-center gap-2'>
                {nginxOk
                  ? <CheckCircle2 size={14} className='text-emerald-400' />
                  : <XCircle size={14} className='text-red-400' />}
                <span className='text-sm text-gray-300'>
                  nginx {statusData.nginx_installed ? t('npm.nginxNainstalovan') : t('npm.nginxChybi')}
                  {statusData.stream_module_loaded ? t('npm.streamModulOk') : t('npm.streamModulChybiParens')}
                </span>
              </div>
              <div className='flex items-center gap-2'>
                {statusData.local_nginx_enabled
                  ? <CheckCircle2 size={14} className='text-emerald-400' />
                  : <AlertCircle size={14} className='text-amber-400' />}
                <span className='text-sm text-gray-300'>
                  {t('npm.lokalniNginx', 'Lokální nginx:')} {statusData.local_nginx_enabled ? t('npm.aktivni2') : t('npm.neaktivni')}
                </span>
              </div>
              <div className='flex items-center gap-2'>
                {statusData.nginx_test_ok
                  ? <CheckCircle2 size={14} className='text-emerald-400' />
                  : <XCircle size={14} className='text-amber-400' />}
                <span className='text-sm text-gray-300'>
                  nginx -t: {statusData.nginx_test_ok ? t('npm.nginxTestOk') : t('npm.nginxTestChyba')}
                </span>
              </div>
              <span className='text-xs text-gray-500 ml-auto'>
                {statusData.stream_blocks_count} {t('npm.blokuVDb')}
              </span>
            </div>
            {!statusData.nginx_test_ok && statusData.nginx_test_out && (
              <pre className='mt-2 text-xs text-red-400 bg-red-500/5 rounded p-2 overflow-auto max-h-24'>
                {statusData.nginx_test_out}
              </pre>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bloky */}
      <Card>
        <CardHeader>
          <CardTitle icon={Server}>{t('npm.streamProxyBloky')}</CardTitle>
          <div className='flex items-center gap-2'>
            <RefreshButton onRefresh={refetch} loading={isFetching} />
            <Button variant='primary' onClick={() => setEditBlock('new')}>
              <Plus size={13} /> {t('npm.pridat')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading
            ? <div className='text-sm text-gray-500 py-4'>{t('npm.nacitam')}</div>
            : blocks.length === 0
            ? (
              <div className='flex flex-col items-center py-10 text-gray-500 gap-2'>
                <Server size={28} className='opacity-30' />
                <p className='text-sm'>{t('npm.zadneStreamBloky')}</p>
                <Button variant='secondary' onClick={() => setEditBlock('new')}>
                  <Plus size={13} /> {t('npm.vytvoritPrvniBlok')}
                </Button>
              </div>
            )
            : (
              <table className='w-full'>
                <thead>
                  <tr className='border-b border-gray-800'>
                    <th className='text-left py-3 px-4 text-xs text-gray-400'>{t('npm.nazev')}</th>
                    <th className='text-left py-3 px-4 text-xs text-gray-400'>Port</th>
                    <th className='text-left py-3 px-4 text-xs text-gray-400'>{t('npm.rezim2')}</th>
                    <th className='text-left py-3 px-4 text-xs text-gray-400'>Backend</th>
                    <th className='text-left py-3 px-4 text-xs text-gray-400'>Stav</th>
                    <th className='py-3 px-4' />
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-800'>
                  {blocks.map((b) => (
                    <tr key={b.id} className='hover:bg-gray-800/30'>
                      <td className='py-3 px-4'>
                        <p className='text-sm font-medium text-white font-mono'>{b.name}</p>
                        {b.description && <p className='text-xs text-gray-500'>{b.description}</p>}
                      </td>
                      <td className='py-3 px-4'>
                        <span className='text-sm font-mono text-gray-300'>:{b.listen_port}</span>
                      </td>
                      <td className='py-3 px-4'>
                        <span className='text-xs px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400'>
                          {MODE_LABEL_KEYS[b.mode] ? t(MODE_LABEL_KEYS[b.mode]) : b.mode}
                        </span>
                      </td>
                      <td className='py-3 px-4'>
                        <span className='text-sm font-mono text-gray-300'>{b.backend}</span>
                        {b.sni_pattern && (
                          <p className='text-xs text-gray-500 font-mono'>SNI: {b.sni_pattern}</p>
                        )}
                      </td>
                      <td className='py-3 px-4'>
                        <div className='flex items-center gap-2'>
                          {b.enabled
                            ? (
                              <span className='flex items-center gap-1 text-xs text-emerald-400'>
                                <CheckCircle2 size={12} /> {t('npm.aktivni2')}
                              </span>
                            )
                            : (
                              <span className='flex items-center gap-1 text-xs text-gray-500'>
                                <XCircle size={12} /> {t('npm.neaktivni')}
                              </span>
                            )}
                          {!b.conf_exists && b.enabled && (
                            <span
                              className='text-xs text-amber-400'
                              title={t('npm.confSouborChybiNa')}
                            >
                              {t('npm.confChybi')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className='py-3 px-4'>
                        <div className='flex items-center justify-end gap-1'>
                          <button
                            type='button'
                            onClick={() => toggle.mutate({ name: b.name, enabled: !b.enabled })}
                            className='p-1.5 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white'
                            title={b.enabled ? t('npm.deaktivovat') : t('npm.aktivovat')}
                          >
                            {b.enabled ? <PowerOff size={14} /> : <Power size={14} />}
                          </button>
                          <button
                            type='button'
                            onClick={() => setEditBlock(b)}
                            className='p-1.5 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white'
                            title={t('common.edit', 'Upravit')}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type='button'
                            onClick={() => confirmDelete(b.name)}
                            className='p-1.5 rounded-lg hover:bg-red-500/20 text-gray-400 hover:text-red-400'
                            title={t('common.delete', 'Smazat')}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </CardContent>
      </Card>

      {editBlock !== null && (
        <StreamBlockModal
          block={editBlock === 'new' ? {} : editBlock}
          onClose={() => setEditBlock(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ── Nginx configs on disk (read-only) ───────────────────────────────────────


// ── Settings tab ──────────────────────────────────────────────────────────────

function SettingsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editInst, setEditInst] = useState<NpmInstance | null | 'new'>(null);
  const [testResults, setTestResults] = useState<Record<string, { ok: boolean; error?: string }>>(
    {},
  );

  const { data: resp, isLoading, refetch } = useQuery<{ ok: boolean; instances: NpmInstance[] }>({
    queryKey: ['npm-instances'],
    queryFn: () => callMcp('npm.instances_list', {}),
  });
  const instances = resp?.instances ?? [];
  const advertisedEdges =
    (resp as { advertised_edges?: AdvertisedEdge[] } | undefined)?.advertised_edges ?? [];

  // Převzetí nalezené edge z infrastruktury (npm.edge_use) — stejná cesta
  // jako amber nabídka v přehledu, tady v „přidat instanci" flow.
  const adoptEdge = useMutation({
    mutationFn: async (stableId: string) => {
      const r = await callMcp('npm.edge_use', { hostname: stableId, set_default: true }) as {
        ok?: boolean;
        error?: string;
      };
      if (r?.ok === false) throw new Error(r.error ?? 'Adopce edge selhala');
      return r;
    },
    onSuccess: () => {
      toast.success(t('npm.edgeAdopted', 'Edge přijata jako nadřazená proxy'));
      setEditInst(null);
      qc.invalidateQueries({ queryKey: ['npm-instances'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Proxy mode A/B — odstraněn 2026-05-14 (sjednocení).
  // Nahrazeno: internal vždy + volitelný edge container (viz tab "Edge proxy").

  const upsert = useMutation({
    mutationFn: (data: NpmInstance & { password?: string }) =>
      callMcp('npm.instance_upsert', { ...data }),
    onSuccess: () => {
      toast.success(t('npm.instanceUlozena'));
      setEditInst(null);
      qc.invalidateQueries({ queryKey: ['npm-instances'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => callMcp('npm.instance_delete', { id }),
    onSuccess: () => {
      toast.success(t('npm.instanceOdebrana'));
      qc.invalidateQueries({ queryKey: ['npm-instances'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setDefault = useMutation({
    mutationFn: (id: string) => callMcp('npm.instance_set_default', { id }),
    onSuccess: () => {
      toast.success(t('npm.vychoziInstanceNastavena'));
      qc.invalidateQueries({ queryKey: ['npm-instances'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function testInstance(id: string) {
    try {
      const result = await callMcp<{ ok: boolean; error?: string }>(
        'npm.instance_test',
        { id },
      );
      setTestResults((p) => ({ ...p, [id]: result }));
      if (result.ok) toast.success(`Instance ${id}: OK`);
      else toast.error(`Instance ${id}: ${result.error}`);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setTestResults((p) => ({ ...p, [id]: { ok: false, error: msg } }));
      toast.error(msg);
    }
  }

  const upstream = instances.filter((i) => i.is_upstream);

  return (
    <div className='space-y-5'>

      {/* Lokální nginx — fixní, nesmazatelný */}
      {(() => {
        const localInst = instances.find((i) => i.id === 'local');
        return localInst
          ? (
            <Card>
              <CardHeader>
                <CardTitle icon={Server}>{t('npm.lokalniProxy')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='flex items-center gap-3 px-3 py-2.5 rounded-xl bg-emerald-900/10 border border-emerald-800/30'>
                  <div className='w-2 h-2 rounded-full bg-emerald-400 shrink-0' />
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2'>
                      <span className='text-sm font-medium text-white'>{localInst.label}</span>
                      <span className='text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400'>
                        {t('npm.tentoNode')}
                      </span>
                    </div>
                    <div className='flex items-center gap-1.5 mt-0.5'>
                      <Link2 size={10} className='text-gray-600' />
                      <span className='text-xs text-gray-400 font-mono'>{localInst.url}</span>
                    </div>
                  </div>
                </div>
                <p className='text-xs text-gray-500 mt-2'>
                  {t('npm.interniNginxNaTomto')}
                </p>
              </CardContent>
            </Card>
          )
          : null;
      })()}

      {/* Nadřazené NPM instance */}
      <Card>
        <CardHeader>
          <CardTitle icon={Globe}>{t('npm.nadrazeneProxy')}</CardTitle>
          <div className='flex items-center gap-2'>
            <RefreshButton onRefresh={refetch} loading={isLoading} />
            <Button variant='primary' size='sm' onClick={() => setEditInst('new')}>
              <Plus size={13} /> {t('npm.rucni')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading
            ? <p className='text-sm text-gray-500 py-4'>{t('npm.nacitam')}</p>
            : (
              <div className='space-y-1.5'>
                {upstream.map((inst) => (
                  <InstanceRow
                    key={inst.id}
                    inst={inst}
                    testResult={testResults[inst.id]}
                    onEdit={() => setEditInst(inst)}
                    onDelete={async () => {
                      if (await themedConfirm(`Odebrat instanci ${inst.label}?`)) remove.mutate(inst.id);
                    }}
                    onTest={() => testInstance(inst.id)}
                    onSetDefault={() => setDefault.mutate(inst.id)}
                  />
                ))}
                {/* Nalezené v infrastruktuře — PŘÍMO v kartě, ne schované za
                    „Ruční" (Filip 2026-09-02). Nepřevzaté edge s tlačítkem. */}
                {advertisedEdges.filter((e) => !e.adopted).map((e) => (
                  <div
                    key={e.key}
                    className='flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-amber-500/30 bg-amber-500/5'
                  >
                    <Globe size={12} className='text-amber-400' />
                    <div className='flex-1'>
                      <span className='text-sm text-gray-200'>{e.hostname}</span>
                      <span className='text-[10px] text-gray-500 ml-2'>
                        {t('npm.discoveredInInfra', 'Nalezené v infrastruktuře')}
                        {e.public_ip ? ` · ${e.public_ip}` : ''}
                      </span>
                    </div>
                    <button
                      type='button'
                      disabled={adoptEdge.isPending}
                      onClick={() => adoptEdge.mutate(e.stable_id)}
                      className='text-[10px] px-2 py-1 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 transition-colors'
                    >
                      {adoptEdge.isPending
                        ? t('npm.edgeAdopting', 'Přijímám…')
                        : t('npm.edgeTakeFromInfra', 'Převzít z infrastruktury')}
                    </button>
                  </div>
                ))}
                {upstream.length === 0 &&
                  advertisedEdges.filter((e) => !e.adopted).length === 0 && (
                  <div className='py-8 text-center space-y-2'>
                    <AlertCircle size={24} className='text-amber-400 mx-auto' />
                    <p className='text-sm text-amber-300'>{t('npm.zadneNadrazeneNpmInstance')}</p>
                    <p className='text-xs text-gray-500'>
                      {t('npm.vRezimuBJe')}
                    </p>
                  </div>
                )}
              </div>
            )}
        </CardContent>
      </Card>

      {editInst === 'new' && (
        <InstanceModal
          inst={null}
          onClose={() => setEditInst(null)}
          onSave={(d) => upsert.mutate(d)}
          advertised={advertisedEdges}
          onAdopt={(sid) => adoptEdge.mutate(sid)}
          adopting={adoptEdge.isPending}
        />
      )}
      {editInst && editInst !== 'new' && (
        <InstanceModal
          inst={editInst as NpmInstance}
          onClose={() => setEditInst(null)}
          onSave={(d) => upsert.mutate(d)}
        />
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function TemplateFormModal({ template, onClose, onSaved }: {
  template?: ProxyTemplate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = !!template;
  const [key, setKey] = useState(String(template?.key ?? ''));
  const [label, setLabel] = useState(String(template?.label ?? ''));
  const [description, setDescription] = useState(String(template?.description ?? ''));
  const [forwardScheme, setForwardScheme] = useState(String(template?.forward_scheme ?? 'http'));
  const [ssl, setSsl] = useState(template?.ssl !== false);
  const [http2, setHttp2] = useState(template?.http2 !== false);
  const [websockets, setWebsockets] = useState(!!template?.websockets);
  const [hsts, setHsts] = useState(!!template?.hsts_enabled);
  const [blockExploits, setBlockExploits] = useState(template?.block_exploits !== false);
  const [advancedConfig, setAdvancedConfig] = useState(String(template?.advanced_config ?? ''));

  const inp =
    'w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500';
  const toggle = (v: boolean, set: (b: boolean) => void, lbl: string) => (
    <label className='flex items-center gap-2 cursor-pointer'>
      <input type='checkbox' checked={v} onChange={(e) => set(e.target.checked)} />
      <span className='text-xs text-gray-400'>{lbl}</span>
    </label>
  );

  const save = useMutation({
    mutationFn: () =>
      callMcp('npm.template_create', {
        module: isEdit ? String(template?.module ?? 'custom') : 'custom',
        key,
        label,
        description,
        forward_scheme: forwardScheme,
        ssl,
        http2,
        websockets,
        hsts_enabled: hsts,
        block_exploits: blockExploits,
        advanced_config: advancedConfig || undefined,
        custom_headers: [],
      }),
    onSuccess: () => {
      toast.success(isEdit ? t('npm.sablonaUlozena') : t('npm.sablonaVytvorenaToast'));
      onSaved();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <Modal open onClose={onClose} title={isEdit ? t('npm.upravitSablonu') : t('npm.novaSablona')}>
      <div className='space-y-3'>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <label className='text-xs text-gray-500 mb-1 block'>{t('npm.klicSlug')}</label>
            <input
              value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder='my-template'
              className={inp}
              disabled={isEdit}
            />
          </div>
          <div>
            <label className='text-xs text-gray-500 mb-1 block'>{t('npm.nazev')}</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('npm.mojeSablona')}
              className={inp}
            />
          </div>
        </div>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>Popis</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={inp}
          />
        </div>
        <div className='flex flex-wrap gap-4 py-2'>
          {toggle(ssl, setSsl, 'SSL')}
          {toggle(http2, setHttp2, 'HTTP/2')}
          {toggle(websockets, setWebsockets, 'WebSocket')}
          {toggle(hsts, setHsts, 'HSTS')}
          {toggle(blockExploits, setBlockExploits, 'Block Exploits')}
        </div>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>{t('npm.forwardScheme')}</label>
          <Select
            value={forwardScheme}
            onChange={(e) => setForwardScheme(e.target.value)}
          >
            <option value='http'>http</option>
            <option value='https'>https</option>
          </Select>
        </div>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>{t('npm.advancedNginxConfig')}</label>
          <textarea
            value={advancedConfig}
            onChange={(e) => setAdvancedConfig(e.target.value)}
            rows={5}
            className={inp + ' font-mono text-xs'}
            placeholder={t('npm.customNginxDirectives')}
          />
        </div>
        <div className='flex justify-end gap-2'>
          <Button variant='ghost' onClick={onClose}>{t('npm.zrusit')}</Button>
          <Button
            variant='primary'
            loading={save.isPending}
            onClick={() => save.mutate()}
            disabled={!key || !label}
          >
            {isEdit ? t('npm.ulozit') : t('npm.vytvorit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function TemplatesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: resp, isLoading } = useQuery<{ ok: boolean; templates: ProxyTemplate[] }>({
    queryKey: ['npm-templates'],
    queryFn: () => callMcp('npm.templates_list', {}),
  });
  const templates = resp?.templates ?? [];
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editTpl, setEditTpl] = useState<ProxyTemplate | null>(null);

  const deleteMut = useMutation({
    mutationFn: (t: { module: string; key: string }) => callMcp('npm.template_delete', t),
    onSuccess: () => {
      toast.success(t('npm.sablonaSmazana'));
      qc.invalidateQueries({ queryKey: ['npm-templates'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (isLoading) return <p className='text-sm text-gray-500'>{t('npm.nacitam2')}</p>;

  const grouped = templates.reduce((acc, t) => {
    const mod = String(t.module);
    (acc[mod] ??= []).push(t);
    return acc;
  }, {} as Record<string, typeof templates>);

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Layers}>{t('npm.proxySablony', 'Proxy šablony ({{count}})', { count: templates.length })}</CardTitle>
        <Button variant='primary' size='sm' onClick={() => setShowCreate(true)}>
          <Plus size={13} /> {t('npm.novaSablona')}
        </Button>
      </CardHeader>
      <CardContent>
        <p className='text-xs text-gray-500 mb-4'>
          {t(
            'npm.templatesHintPrefix',
            'Konfigurace pro proxy hosty — SSL, WebSocket, HSTS, security, nginx directives. Šablony',
          )}{' '}
          <code className='font-mono text-[10px] bg-gray-800 px-1 rounded'>module-seed</code>{' '}
          {t('npm.templatesHintMid', 'se aktualizují z modulů. Vlastní')}
          {' ('}
          <code className='font-mono text-[10px] bg-gray-800 px-1 rounded'>user-edited</code>
          {t('npm.templatesHintSuffix', ') zůstávají nedotčené.')}
        </p>
        <div className='space-y-3'>
          {Object.entries(grouped).map(([mod, ts]) => (
            <div key={mod}>
              <p className='text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-2'>
                {mod}
              </p>
              <div className='space-y-1.5'>
                {ts.map((tpl) => {
                  const key = `${mod}/${tpl.key}`;
                  const isOpen = expanded === key;
                  const isUserEdited = tpl.source === 'user-edited';
                  return (
                    <div
                      key={key}
                      className='rounded-xl border border-gray-800 bg-gray-800/30 overflow-hidden'
                    >
                      <button
                        type='button'
                        onClick={() => setExpanded(isOpen ? null : key)}
                        className='w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-800/60 transition-colors'
                      >
                        <Layers size={13} className='text-gray-500 shrink-0' />
                        <div className='flex-1 min-w-0'>
                          <span className='text-sm font-medium text-white'>{String(tpl.label)}</span>
                          <span className='ml-2 text-[10px] font-mono text-gray-500'>
                            {mod}/{String(tpl.key)}
                          </span>
                        </div>
                        <div className='flex items-center gap-1'>
                          {(tpl as any).ssl && (
                            <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400'>
                              SSL
                            </span>
                          )}
                          {(tpl as any).http2 && (
                            <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400'>
                              {t('npm.http2')}
                            </span>
                          )}
                          {(tpl as any).websockets && (
                            <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-400'>
                              WS
                            </span>
                          )}
                          {(tpl as any).hsts_enabled && (
                            <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400'>
                              HSTS
                            </span>
                          )}
                          {(tpl as any).block_exploits && (
                            <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-orange-500/15 text-orange-400'>
                              SEC
                            </span>
                          )}
                        </div>
                        <span className='text-xs text-gray-600'>{isOpen ? '▼' : '▶'}</span>
                      </button>
                      {isOpen && (
                        <div className='px-4 py-3 bg-gray-900/50 border-t border-gray-800 space-y-2 text-xs'>
                          {(tpl as any).description && (
                            <p className='text-gray-400'>{String((tpl as any).description)}</p>
                          )}
                          <div className='grid grid-cols-2 gap-2 text-[11px]'>
                            <div>
                              <span className='text-gray-500'>{t('npm.forwardScheme2')}</span>{' '}
                              <code className='font-mono text-gray-300'>
                                {String((tpl as any).forward_scheme || 'http')}
                              </code>
                            </div>
                            <div>
                              <span className='text-gray-500'>{t('npm.source')}</span>{' '}
                              <code className='font-mono text-gray-400'>
                                {String((tpl as any).source || 'system')}
                              </code>
                            </div>
                          </div>
                          {(tpl as any).advanced_config && (
                            <pre className='text-[10px] text-gray-400 bg-black/30 rounded p-2 overflow-auto max-h-40 font-mono'>{String((tpl as any).advanced_config)}</pre>
                          )}
                          {(tpl as any).custom_headers && Array.isArray((tpl as any).custom_headers) &&
                            (tpl as any).custom_headers.length > 0 && (
                            <div>
                              <p className='text-gray-500 text-[10px] uppercase tracking-wide mb-1'>
                                {t('npm.headers')}
                              </p>
                              <ul className='text-[10px] font-mono text-gray-400 space-y-0.5'>
                                {(tpl as any).custom_headers.map((h: any, i: number) => (
                                  <li key={i}>
                                    {h.name}: <span className='text-gray-300'>{h.value}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className='flex items-center gap-2 pt-2 border-t border-gray-800/30'>
                            <span
                              className={`text-[9px] px-1.5 py-0.5 rounded ${
                                isUserEdited
                                  ? 'bg-blue-500/10 text-blue-400'
                                  : 'bg-gray-800 text-gray-500'
                              }`}
                            >
                              {String(tpl.source ?? 'module-seed')}
                            </span>
                            <div className='ml-auto flex gap-1'>
                              <Button
                                variant='ghost'
                                size='sm'
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setEditTpl(tpl);
                                }}
                              >
                                <Pencil size={11} />
                              </Button>
                              {isUserEdited && (
                                <Button
                                  variant='danger'
                                  size='sm'
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await themedConfirm(t('npm.smazatSablonuConfirm', { label: tpl.label })) &&
                                      deleteMut.mutate({
                                        module: String(tpl.module),
                                        key: String(tpl.key),
                                      });
                                  }}
                                >
                                  <Trash2 size={11} />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      {(showCreate || editTpl) && (
        <TemplateFormModal
          template={editTpl ?? undefined}
          onClose={() => {
            setShowCreate(false);
            setEditTpl(null);
          }}
          onSaved={() => {
            setShowCreate(false);
            setEditTpl(null);
            qc.invalidateQueries({ queryKey: ['npm-templates'] });
          }}
        />
      )}
    </Card>
  );
}

// ── TTY Proxy Tab ────────────────────────────────────────────────────────────

function TtyTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data } = useQuery<{ ok: boolean; entries: ProxyEntry[] }>({
    queryKey: ['proxy-all'],
    queryFn: () =>
      callMcp('npm.proxy_entries_list', {}),
    staleTime: 10_000,
  });

  // Fallback: show known TTY agents from localcode status
  const { data: lcStatus } = useQuery<{
    ok: boolean;
    claudeCode: LocalcodeAgentStatus;
    qwenCode: LocalcodeAgentStatus;
    opencode: LocalcodeAgentStatus;
    claudeLocal: LocalcodeAgentStatus;
    kimiCode: LocalcodeAgentStatus;
  }>({
    queryKey: ['localcode-status'],
    queryFn: () =>
      callMcp('localcode.status_get', {}),
    staleTime: 15_000,
  });

  const ttyEntries = data?.entries?.filter((entry) => entry.proxy_type === 'tty') ?? [];

  const agents = [
    {
      name: 'claude-code',
      label: 'Claude Code',
      port: 7682,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      alive: lcStatus?.claudeCode?.ttyd,
    },
    {
      name: 'qwen-code',
      label: 'Qwen Code',
      port: 7681,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      alive: lcStatus?.qwenCode?.ttyd,
    },
    {
      name: 'opencode',
      label: 'Open Code',
      port: 7683,
      color: 'text-violet-400',
      bg: 'bg-violet-500/10',
      alive: lcStatus?.opencode?.ttyd,
    },
    {
      name: 'claude-local',
      label: 'Claude Local',
      port: 7684,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      alive: lcStatus?.claudeLocal?.ttyd,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Wifi}>
          {t('npm.ttyProxyAktivnich', { count: agents.filter((a) => a.alive).length })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className='text-xs text-gray-500 mb-4'>
          {t('npm.websocketProxyProTerminaly')}
        </p>
        <div className='space-y-2'>
          {agents.map((agent) => {
            const registered = ttyEntries.some((e) => e.name === agent.name);
            return (
              <div
                key={agent.name}
                className={`flex items-center gap-3 p-3 rounded-xl border ${
                  agent.alive ? 'border-gray-700/50 bg-gray-800/20' : 'border-gray-800 opacity-50'
                }`}
              >
                <div
                  className={`w-2 h-8 rounded-full ${
                    agent.alive ? agent.color.replace('text-', 'bg-') : 'bg-gray-700'
                  }`}
                />
                <div className='flex-1'>
                  <div className='flex items-center gap-2'>
                    <span
                      className={`text-sm font-semibold ${
                        agent.alive ? 'text-white' : 'text-gray-600'
                      }`}
                    >
                      {agent.label}
                    </span>
                    <span className='text-[10px] font-mono text-gray-600'>:{agent.port}</span>
                    {registered && (
                      <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400'>
                        {t('npm.proxyBadge')}
                      </span>
                    )}
                  </div>
                  <div className='text-[10px] text-gray-600'>
                    {agent.alive
                      ? t('npm.nginxWsProxyArrow', { port: agent.port })
                      : t('npm.ttydNebezi')}
                  </div>
                </div>
                {/* Ovládání — enable/disable proxy */}
                {agent.alive && !registered && (
                  <Button
                    variant='success'
                    size='sm'
                    onClick={async () => {
                      try {
                        await callMcp('npm.tty_proxy_set', { name: agent.name, enabled: true });
                        toast.success(t('npm.ttyProxyProSeVytvari', { label: agent.label }));
                        qc.invalidateQueries({ queryKey: ['proxy-all'] });
                      } catch {
                        toast.error(t('npm.nepodariloSeZapnoutProxy'));
                      }
                    }}
                  >
                    {t('npm.zapnoutProxy')}
                  </Button>
                )}
                {registered && (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={async () => {
                      try {
                        await callMcp('npm.tty_proxy_set', { name: agent.name, enabled: false });
                        toast.success(t('npm.ttyProxyProSeOdregistruje', { label: agent.label }));
                        qc.invalidateQueries({ queryKey: ['proxy-all'] });
                      } catch {
                        toast.info(t('npm.ttyProxySeOdregistruje'));
                      }
                    }}
                  >
                    {t('npm.vypnoutBtn')}
                  </Button>
                )}
                <div
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    agent.alive ? 'bg-green-500' : 'bg-gray-700'
                  }`}
                />
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main Module Page ─────────────────────────────────────────────────────────

// ── Edge Proxy Tab ──────────────────────────────────────────────────────────
// Toggle pro spuštění/vypnutí lokálního Edge NPM containeru (porty 80/443
// default). Edge se inzeruje v catalog `npm_edges` — peer nody si ho mohou
// přidat jako svou nadřazenou (viz `npm_edge_use` tool).

interface EdgeStatusResp {
  ok: boolean;
  hostname: string;
  local: {
    container_name: string;
    container_status: string;
    admin_api_reachable: boolean;
    admin_url_local: string;
    advertised: boolean;
    catalog_entry: Record<string, unknown> | null;
  };
  mesh: {
    total_edges: number;
    edges: Array<{
      hostname: string;
      public_ip?: string;
      http_proxy_port?: number;
      https_proxy_port?: number;
      admin_url_local?: string;
      owner_node_id?: string;
      started_at?: string;
    }>;
  };
}

function EdgeTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [purgeData, setPurgeData] = useState(false);
  const [enableHttpPort, setEnableHttpPort] = useState(80);
  const [enableHttpsPort, setEnableHttpsPort] = useState(443);

  const { data, isFetching } = useQuery<EdgeStatusResp>({
    queryKey: ['npm-edge-status'],
    queryFn: () => callMcp('npm.edge_status', {}),
    refetchInterval: 10_000,
  });

  const enable = useMutation({
    mutationFn: () =>
      callMcp('npm.edge_enable', {
        http_port: enableHttpPort,
        https_port: enableHttpsPort,
      }),
    onSuccess: (res: unknown) => {
      const r = res as { ok?: boolean; admin_password?: string; admin_setup_status?: string };
      if (r.ok) {
        if (r.admin_setup_status === 'manual_required') {
          toast.warning(t('npm.edgeSpustenAleAdmin'));
        } else if (r.admin_password) {
          toast.success(t('npm.edgeSpustenAdminHeslo', { password: r.admin_password }), { duration: 30_000 });
        } else {
          toast.success(t('npm.edgeSpusten'));
        }
      }
      qc.invalidateQueries({ queryKey: ['npm-edge-status'] });
      qc.invalidateQueries({ queryKey: ['npm-instances'] });
    },
    onError: (e: Error) => toast.error(t('npm.edgeEnableSelhalo', { msg: e.message })),
  });

  const disable = useMutation({
    mutationFn: () => callMcp('npm.edge_disable', { purge_data: purgeData }),
    onSuccess: () => {
      toast.success(purgeData ? t('npm.edgeVypnutDataSmazana') : t('npm.edgeVypnutDataZachovana'));
      setConfirmDisable(false);
      setPurgeData(false);
      qc.invalidateQueries({ queryKey: ['npm-edge-status'] });
      qc.invalidateQueries({ queryKey: ['npm-instances'] });
    },
    onError: (e: Error) => toast.error(t('npm.edgeDisableSelhalo', { msg: e.message })),
  });

  const isEnabled = data?.local.container_status === 'running' && data.local.advertised;
  const isPending = enable.isPending || disable.isPending;

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle>
            <Globe size={16} className='inline mr-2' />
            {t('npm.edgeProxyNaTomto')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-gray-400 mb-4'>
            {t('npm.edgeProxyPopis')} <code className='text-xs'>catalog npm_edges</code>{' '}
            {t('npm.peerNodySiHoMohou')}
          </p>

          {/* Toggle */}
          <div className='flex items-center justify-between gap-4 mb-4 p-3 bg-gray-900/40 rounded-lg'>
            <div className='flex items-center gap-3'>
              <StatusDot
                status={isEnabled ? 'running' : data?.local.container_status === 'not_created' ? 'stopped' : 'pending'}
              />
              <div>
                <div className='text-sm font-medium text-white'>
                  {isEnabled
                    ? t('npm.edgeProxyBezi')
                    : data?.local.container_status === 'not_created'
                    ? t('npm.edgeProxyNeniNasazena')
                    : t('npm.edgeProxyStatus', { status: data?.local.container_status ?? '?' })}
                </div>
                {data?.local.advertised && (
                  <div className='text-xs text-gray-500'>
                    {t('npm.adminUi')} <code>{data.local.admin_url_local}</code>
                  </div>
                )}
              </div>
            </div>
            <AsyncStatusToggle
              value={isEnabled}
              actual={data?.local.admin_api_reachable}
              pending={isPending || isFetching}
              onToggle={(next) => {
                if (next) {
                  return enable.mutateAsync();
                } else {
                  setConfirmDisable(true);
                  return Promise.resolve();
                }
              }}
            />
          </div>

          {/* Ports config (jen když není zapnuté) */}
          {!isEnabled && (
            <div className='grid grid-cols-2 gap-3 mb-2 p-3 bg-gray-900/20 rounded-lg'>
              <div>
                <label className='text-xs text-gray-500 block mb-1'>{t('npm.httpPortVerejny')}</label>
                <input
                  type='number'
                  value={enableHttpPort}
                  onChange={(e) => setEnableHttpPort(parseInt(e.target.value || '80', 10))}
                  min={1}
                  max={65535}
                  className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white'
                />
              </div>
              <div>
                <label className='text-xs text-gray-500 block mb-1'>{t('npm.httpsPortVerejny')}</label>
                <input
                  type='number'
                  value={enableHttpsPort}
                  onChange={(e) => setEnableHttpsPort(parseInt(e.target.value || '443', 10))}
                  min={1}
                  max={65535}
                  className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white'
                />
              </div>
              <div className='col-span-2 text-[11px] text-gray-500'>
                {t('npm.adminPort1881Popis')}
              </div>
            </div>
          )}

          {/* Catalog entry detail */}
          {data?.local.catalog_entry && (
            <details className='mt-3 text-xs'>
              <summary className='cursor-pointer text-gray-500 hover:text-gray-300'>
                {t('npm.inzerceVCatalogMesh')}
              </summary>
              <pre className='mt-2 p-2 bg-gray-900 rounded text-[10px] text-gray-400 overflow-x-auto'>
                {JSON.stringify(data.local.catalog_entry, null, 2)}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>

      {/* Ostatní edges v mesh */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Server size={16} className='inline mr-2' />
            {t('npm.edgesNaprícMesh', { count: data?.mesh.total_edges ?? 0 })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data?.mesh.edges.length ? (
            <EmptyState
              icon={<Globe size={28} />}
              title={t('npm.zadnyEdgeZatimNezarapuje')}
              description={t('npm.zapniEdgeNaAlespon')}
            />
          ) : (
            <div className='space-y-2'>
              {data.mesh.edges.map((edge) => (
                <div
                  key={edge.hostname}
                  className='flex items-center justify-between p-2 bg-gray-900/30 rounded-lg'
                >
                  <div className='flex items-center gap-3'>
                    <Globe size={14} className='text-blue-400' />
                    <div>
                      <div className='text-sm font-medium text-white'>
                        {edge.hostname}
                        {edge.hostname === data.hostname && (
                          <Badge variant='blue' className='ml-2'>{t('npm.tentoNod')}</Badge>
                        )}
                      </div>
                      <div className='text-[11px] text-gray-500'>
                        {edge.public_ip} · porty {edge.http_proxy_port}/{edge.https_proxy_port}
                      </div>
                    </div>
                  </div>
                  {edge.started_at && (
                    <span className='text-[10px] text-gray-500'>
                      {t('npm.odDatumu', { date: new Date(edge.started_at).toLocaleString('cs-CZ') })}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disable confirm modal */}
      <Modal
        open={confirmDisable}
        onClose={() => {
          setConfirmDisable(false);
          setPurgeData(false);
        }}
        title={t('npm.vypnoutEdgeProxy')}
        footer={
          <>
            <Button
              variant='secondary'
              onClick={() => {
                setConfirmDisable(false);
                setPurgeData(false);
              }}
            >
              {t('npm.zrusit')}
            </Button>
            <Button
              variant='danger'
              onClick={() => disable.mutate()}
              disabled={disable.isPending}
            >
              {disable.isPending
                ? t('npm.vypinam')
                : purgeData
                ? t('npm.vypnoutSmazatData')
                : t('npm.vypnoutLabel')}
            </Button>
          </>
        }
      >
        <div className='space-y-3 text-sm'>
          <p className='text-gray-300'>
            {t('npm.edgeProxySeSundaPopis')}{' '}
            <code className='text-xs'>/var/acm/npm-edge/{data?.hostname}/</code>{' '}
            {t('npm.proPripadnyReenable')}
          </p>
          <label className='flex items-center gap-2 cursor-pointer'>
            <input
              type='checkbox'
              checked={purgeData}
              onChange={(e) => setPurgeData(e.target.checked)}
              className='w-4 h-4'
            />
            <span className='text-sm text-red-400'>
              {t('npm.smazatIPersistentData')}
            </span>
          </label>
          {purgeData && (
            <div className='p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-300'>
              {t('npm.priReenableBudesMuset')}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}


// ── Widget entrypoints ───────────────────────────────────────────────────────

export function HostsPanel({ scope }: WidgetRenderProps) {
  setNpmWidgetScope(scope ?? { node: null, identity: null });
  return <HostsTab key={scopeKey()} />;
}
export function StreamPanel({ scope }: WidgetRenderProps) {
  setNpmWidgetScope(scope ?? { node: null, identity: null });
  return <StreamTab key={scopeKey()} />;
}
export function TtyPanel({ scope }: WidgetRenderProps) {
  setNpmWidgetScope(scope ?? { node: null, identity: null });
  return <TtyTab key={scopeKey()} />;
}
export function CertsPanel({ scope }: WidgetRenderProps) {
  setNpmWidgetScope(scope ?? { node: null, identity: null });
  return <CertsTab key={scopeKey()} />;
}
export function TemplatesPanel({ scope }: WidgetRenderProps) {
  setNpmWidgetScope(scope ?? { node: null, identity: null });
  return <TemplatesTab key={scopeKey()} />;
}
export function EdgePanel({ scope }: WidgetRenderProps) {
  setNpmWidgetScope(scope ?? { node: null, identity: null });
  return <EdgeTab key={scopeKey()} />;
}
export function SettingsPanel({ scope }: WidgetRenderProps) {
  setNpmWidgetScope(scope ?? { node: null, identity: null });
  return <SettingsTab key={scopeKey()} />;
}
